/**
 * Cliente HTTP único de la aplicación. Todo pasa por aquí.
 * La cookie de sesión es httpOnly: se envía sola con credentials:'include'.
 *
 * ⚠️ Este archivo es la RAÍZ de la capa cliente y no importaba NADA local. Desde
 * el 2026-08-26 importa `./subida-partes` para las evidencias de Negociaciones,
 * que ya no tienen tope de peso, y desde el 2026-08-31 `./errores`, que es el
 * texto que lee la persona cuando algo falla. Los DOS son hojas: ninguno importa
 * de aquí (`subida-partes` recibe el POST inyectado y `errores` no importa
 * nada), así que sigue sin haber ciclos.
 */
import {
  descargarPorPartes, subirPorPartes, type Poster,
} from './subida-partes';
import { avisarSiCaducoLaSesion, mensajeDeFallo } from './errores';
import type { BolsaNego } from './nego-bolsa';
import type { LineaBbk, PrecioBbk, SkuBbk } from './nego-bbk';
import type { OpcionTrimestre, Trimestre } from './nego-trimestre';
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detalle?: unknown,
    /**
     * El `codigo` que traiga la respuesta, para distinguir un error de otro sin
     * leerle el TEXTO —que está escrito para una persona y se reescribe sin
     * saber que algo lo estaba parseando—. Hoy los manda `CODIGO_CAMBIO_REQUERIDO`
     * (el portón de contraseña, `src/lib/clave.ts`) y los dos de sesión
     * (`src/lib/errores.ts`).
     */
    readonly codigo?: string,
    /**
     * Lo que dijo el servidor, TAL CUAL, antes de traducirlo. `message` es lo
     * que lee la persona; esto es lo que hace falta para diagnosticar, y por eso
     * no se pierde: en el caso del asesor 518 (2026-08-31) la diferencia entre
     * «No autenticado» y «Sesión inválida o expirada» fue el diagnóstico entero.
     */
    readonly textoServidor?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * El error de una respuesta que no vino bien, con el texto ya traducido, y el
 * aviso de caducidad disparado si tocaba.
 *
 * Es `export` porque `pedir` no es el único que llama al portal: las descargas
 * binarias (actas en PDF, libros de Excel, adjuntos) hacen su propio `fetch`
 * porque necesitan el `blob`, y sin esto cada una inventaba su mensaje.
 */
export async function fallaDeRespuesta(res: Response): Promise<ApiError> {
  const cuerpo = res.headers.get('content-type')?.includes('json')
    ? await res.json().catch(() => null)
    : null;
  return fallaDeCuerpo(res.status, cuerpo, res.statusText);
}

/**
 * Lo mismo, para quien YA leyó el cuerpo y no puede volver a leerlo: un `Response`
 * se consume una sola vez. Hoy la usa la carga de NC Farmatodo, que mira el 409
 * —«semana ya cargada»— antes de decidir si esto es un error o una pregunta.
 */
export function fallaDeCuerpo(
  status: number,
  cuerpo: { error?: string; codigo?: string; detalle?: unknown } | null,
  respaldo = '',
): ApiError {
  const codigo = cuerpo?.codigo;
  const texto = cuerpo?.error;
  const mensaje = mensajeDeFallo(status, texto ?? respaldo, { codigo });
  avisarSiCaducoLaSesion(status, codigo, mensaje);
  return new ApiError(status, mensaje, cuerpo?.detalle, codigo, texto);
}

/**
 * El error de una petición que ni siquiera llegó a tener respuesta: `fetch`
 * lanza. Se marca con `status: 0`, la misma convención que usa el teléfono.
 *
 * `navigator.onLine` distingue las dos causas, y la distinción importa: «no
 * tienes internet» y «el portal no responde» se arreglan en sitios distintos.
 * Es un dato del navegador y puede mentir en un sentido —dice `true` con un
 * wifi conectado que no navega—, nunca en el otro: si dice `false`, no hay red.
 * Por eso solo se AFIRMA la falta de internet cuando dice `false`.
 */
export function fallaDeRed(e: unknown): ApiError {
  const enLinea = typeof navigator === 'undefined' ? undefined : navigator.onLine;
  return new ApiError(0, mensajeDeFallo(0, null, { enLinea }), e, undefined,
    e instanceof Error ? e.message : String(e));
}

/**
 * `fetch` contra el portal con la cookie puesta y el fallo YA TRADUCIDO, para
 * quien necesita la `Response` entera y no el JSON: las descargas binarias
 * (actas en PDF, libros de Excel, CSV) y las respuestas por líneas.
 *
 * Existe porque esos sitios —once: dos en Catálogos, dos en Información de
 * Interés y siete repartidos entre Devoluciones, Financiero, NC Farmatodo y
 * Descargas— hacían cada uno su propio `fetch` y su propio error, y lo que
 * enseñaban era `HTTP 401` o «Failed to fetch»: el peor mensaje del portal en
 * el sitio donde peor cae, con la persona esperando un archivo que pidió. Ahora
 * dicen lo mismo que el resto y disparan el mismo regreso al acceso.
 */
export async function pedirRespuesta(ruta: string, init?: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(ruta, { credentials: 'include', ...init });
  } catch (e) {
    throw fallaDeRed(e);
  }
  if (!res.ok) throw await fallaDeRespuesta(res);
  return res;
}

async function pedir<T>(ruta: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${ruta}`, {
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (e) {
    throw fallaDeRed(e);
  }

  if (!res.ok) throw await fallaDeRespuesta(res);

  return (res.headers.get('content-type')?.includes('json')
    ? await res.json().catch(() => null)
    : null) as T;
}

export const api = {
  get: <T>(ruta: string, params?: Record<string, string | number | undefined>) => {
    const qs = params
      ? '?' + new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== '')
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';
    return pedir<T>(ruta + qs);
  },
  // `exactOptionalPropertyTypes` prohíbe pasar `body: undefined` explícito, así
  // que la propiedad se omite del objeto cuando no hay cuerpo.
  post: <T>(ruta: string, body?: unknown) =>
    pedir<T>(ruta, {
      method: 'POST',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
};

// ─────────── Tipos compartidos con las funciones serverless ───────────

export interface Alcance {
  empresas: number[] | null;
  regiones: number[] | null;
  vendedores: string[] | null;
  clientes: string[] | null;
  grupos: string[] | null;
}

export interface Sesion {
  usuario: {
    username: string;
    nombre: string;
    /** `true` = la clave que usa es la temporal que se le entregó (db/001). */
    debeCambiar?: boolean;
    /**
     * db/080 · Ya puso una clave propia Y sigue siendo la suya. Es el CANDADO
     * del recorrido guiado: la usuaria pidió que el recorrido aparezca «una vez
     * que los usuarios cambien la contraseña del portal a una personalizada».
     * Sale de una columna generada en la base, así que no se recalcula aquí.
     *
     * Opcionales las tres porque el servidor del DEMO (`demo/servidor.ts`) arma
     * sesiones sin ellas: un campo obligatorio rompería esa compilación.
     */
    clavePersonalizada?: boolean;
    /** db/080 · Ya vio el recorrido alguna vez, así que no vuelve a saltar solo. */
    recorridoVisto?: boolean;
  };
  roles: string[];
  modulos: string[];
  paginas: string[];
  alcance: Alcance;
  /**
   * Presente SOLO cuando se entró con la clave maestra (db/023_clave_maestra.sql):
   * `usuario.username` es la cuenta que se está viendo y esto es quién la abrió.
   * Lo devuelven tanto `/auth/login` como `/auth/me`, y de lo segundo depende que
   * el aviso sobreviva a un F5.
   */
  suplantacion?: { usuario: string; nombre: string };
  /**
   * 2026-10-05 · Segundos que le quedan a la sesión cuando el servidor contestó.
   * Con esto `ProveedorSesion` la cierra sola al vencer. Opcional por el DEMO.
   */
  venceEnSeg?: number;
}

export interface FilaVentaMensual {
  anio: number;
  mes: number;
  dim: string | null;
  cajas: number;
  unidades: number;
  importe_usd: number;
  n_facturas: number;
  n_clientes: number;
}

export const authApi = {
  login: (username: string, password: string) =>
    api.post<Sesion>('/auth/login', { username, password }),
  me: () => api.get<Sesion>('/auth/me'),
  logout: () => api.post<{ ok: true }>('/auth/logout'),
  /**
   * db/080 · El usuario pone su propia clave. Devuelve el estado NUEVO leído de
   * la base (no calculado en la pantalla), que es lo que desbloquea el botón
   * del recorrido en el mismo momento, sin recargar.
   */
  cambiarClave: (actual: string, nueva: string) =>
    api.post<{ ok: true; usuario: Sesion['usuario'] }>('/auth/password', { actual, nueva }),
  /** db/080 · «Ya vi el recorrido». Idempotente: solo guarda la primera vez. */
  recorridoVisto: () => api.post<{ ok: true; guardado: boolean }>('/auth/recorrido'),

  /**
   * db/106 · «¿Olvidó su contraseña?». Los TRES únicos llamados del portal que
   * salen SIN sesión aparte del login.
   *
   * `olvide` contesta SIEMPRE lo mismo y con 200 —exista el usuario o no, tenga
   * correo o no—, así que no hay ninguna rama que la pantalla pueda leer para
   * saber si el correo salió: eso es a propósito (decisión 6 de db/106) y quien
   * toque esto no debe «mejorarlo» devolviendo detalles.
   */
  olvide: (usuario: string) =>
    api.post<{ ok: true; aviso: string }>('/auth/olvide', { usuario }),

  /**
   * ¿Sirve todavía el enlace del correo? Se pregunta al abrir la pantalla, antes
   * de pedirle al usuario que se invente una contraseña. Lanza `ApiError` con el
   * texto ya escrito si el enlace está muerto.
   */
  enlaceClave: (t: string) =>
    api.get<{
      ok: true; usuario: string; nombre: string; minimo: number;
      reglas: Array<{ id: string; texto: string }>;
    }>('/auth/reiniciar', { t }),

  /** Canjea el enlace: pone la clave nueva y lo quema. NO devuelve sesión. */
  reiniciarClave: (t: string, nueva: string) =>
    api.post<{ ok: true; usuario: string; aviso: string }>('/auth/reiniciar', { t, nueva }),
};

export interface ResumenDiario {
  meta: {
    empresa: 'GENERAL' | 'DDH' | 'DHV' | 'DIFRANCA';
    metrica: 'cajas' | 'unidades';
    desde: string; hasta: string; fmin: string; fmax: string;
  };
  serie: { f: string; v: number }[];
  seriePrev: { f: string; v: number }[] | null;
  porEmpresa: { nombre: string; v: number }[];
  porRegion: { nombre: string; v: number }[];
  porMarca: { nombre: string; v: number }[];
  topClientes: { etiqueta: string; v: number }[];
  vendedoresActivos: number;
  totales: { prevMes: number | null; prevAnio: number | null };
}

export const resumenApi = {
  diario: (p: { desde?: string; hasta?: string; empresa?: string; yoy?: string }) =>
    api.get<ResumenDiario>('/resumen/diario', p as Record<string, string | undefined>),
};

export interface FilaMatriz {
  ven: string; caj: number; cajP: number | null; g: number | null; cv: number;
  tendencia: 'Creciente' | 'Estable' | 'Decreciente';
  consistencia: 'Muy regular' | 'Regular' | 'Irregular';
}

export interface VendedoresDiario {
  meta: {
    pagina: string; desde: string; hasta: string; fmin: string; fmax: string;
    vendedor: string;
  };
  ranking: { ven: string; v: number }[];
  rankingPrev: { ven: string; v: number }[];
  totalGrupo: number;
  serie: { f: string; v: number }[];
  seriePrev: { f: string; v: number }[] | null;
  porMarca: { nombre: string; v: number }[];
  porRegion: { nombre: string; v: number }[];
  topClientes: { etiqueta: string; v: number }[];
  matriz: FilaMatriz[] | null;
}

export const vendedoresApi = {
  diario: (p: {
    pagina: 'vendedor' | 'vd-evolucion' | 'vd-analisis' | 'vd-comportamiento';
    desde?: string; hasta?: string; vendedor?: string; yoy?: string; matriz?: string;
  }) => api.get<VendedoresDiario>('/vendedores/diario',
    p as Record<string, string | undefined>),
};

/** Un mes de la ventana. Desde D33 la ventana cruza años, así que el año viaja
 *  pegado al mes en todas partes: `meta.periodos`, `serie` y `seriePrev`. */
export interface MarcaPeriodo { anio: number; mes: number }

export interface MarcaDetalle {
  meta: {
    marca: string; etiqueta: string; metrica: 'cajas' | 'unidades';
    /** Año del extremo superior de la ventana (rótulos «vs año anterior»). */
    anio: number;
    /** Los años que toca la ventana, de menor a mayor. */
    anios: number[];
    /** LA VENTANA, mes a mes y en orden. Es lo que pinta la página. */
    periodos: MarcaPeriodo[];
    /** `true` si la ventana abarca más de un año (el caso por defecto). */
    multiAnio: boolean;
    desde: string; hasta: string; rango: { min: string; max: string };
    empresas: number[];
    filtros: {
      /** La región (`'1'`…`'6'`) o una de sus zonas (`'capital'`, `'oriente'`). */
      region: string | null; estado: string | null; ciudad: number | null;
      /** El asesor: `factura_linea.vendedor_cod` (2026-10-19). */
      vendedor: string | null;
      grupo: string | null; cliente: string | null; sucursal: string | null;
      categoria: number | null; sku: string | null;
    };
  };
  serie: (MarcaPeriodo & { v: number })[];
  /** El mismo mes del año anterior, período a período (solo con `yoy=1`). */
  seriePrev: (MarcaPeriodo & { v: number })[] | null;
  totales: {
    total: number; totalPrev: number | null; grupo: number; share: number | null;
    prevMes: number | null; prevAnio: number | null; difrancaUnidades: number | null;
  };
  porEmpresa: { etiqueta: string; v: number }[];
  porRegion: { etiqueta: string; v: number }[];
  porMarca: { etiqueta: string; v: number }[];
  porCategoria: { etiqueta: string; v: number }[];
  topClientes: { etiqueta: string; v: number }[];
  topSku: { etiqueta: string; v: number }[];
  filtros: {
    /** `id` es la región (`'3'`) o una de sus zonas (`'capital'`, `'oriente'`),
     *  que vienen justo debajo de la suya en la lista. */
    regiones: { id: string; nombre: string }[];
    estados: { nombre: string }[];
    ciudades: { id: number; nombre: string; estado: string }[];
    /** Los asesores que facturaron la marca, dentro del alcance. `nombre` es
     *  `null` en los códigos viejos que no lo tienen registrado. */
    asesores: { cod: string; nombre: string | null }[];
    /*
     * `nombre` es lo que viaja en el filtro; `etiqueta` es lo que se pinta.
     * Ninguno de los dos ejes ES un código —la cadena y la razón social
     * agrupan varios—, así que la etiqueta trae el código cuando es UNO solo
     * y cuántos son cuando hay varios (2026-08-26, pedido por la usuaria).
     */
    /** Cadenas comerciales (`grupo_comercial`). Solo cadenas reales. */
    grupos: { nombre: string; etiqueta: string }[];
    /** Razón social: agrupa las sucursales que comparten nombre. */
    clientes: { nombre: string; etiqueta: string }[];
    /** Punto de venta suelto: `cod_cli` con su razón social. */
    sucursales: { cod: string; nombre: string }[];
    categorias: { id: number; nombre: string }[];
    skus: { cod: string; nombre: string }[];
  };
  marcas: { key: string; label: string }[];
}

/**
 * El cuadro de datos de Marcas: una fila por combinación de las columnas
 * (`dims`) elegidas, con cajas y unidades sumadas. La ventana (`periodos`) es
 * la que /marcas/detalle devolvió en `meta` — así el cuadro y la página no
 * pueden discrepar.
 *
 * **No hay recorte**: el endpoint entrega las `meta.nGrupos` combinaciones del
 * filtro, en bocados de `meta.tanda` que se piden con `desde`. `filas` son
 * ARREGLOS posicionales en el orden de `columnas` (las dimensiones pedidas y
 * luego `cajas` y `unidades`), no objetos — ver el punto 6 de la cabecera del
 * endpoint: con nombres de clave, un bocado rozaría los 6 MB del borde.
 */
export interface MarcaCuadro {
  meta: {
    marca: string; etiqueta: string; metrica: 'cajas' | 'unidades';
    periodos: MarcaPeriodo[]; dims: string[];
    /** Filas por bocado, y desde cuál empieza ÉSTE. */
    tanda: number; desde: number;
    /** Las combinaciones que existen: cuántas filas hay que juntar en total. */
    nGrupos: number;
    totalCajas: number; totalUnidades: number;
    /**
     * USD neto de IVA (`factura_linea.importe_usd`); 2020 vale 0 en la fuente.
     * Solo viene si se pidió `importe=1` (y entonces `columnas` trae `importe`).
     */
    totalImporte?: number;
    empresas: number[];
    filtros: {
      region: string | null; estado: string | null; ciudad: number | null;
      vendedor: string | null;
      grupo: string | null; cliente: string | null; sucursal: string | null;
      categoria: number | null; sku: string | null;
    };
  };
  columnas: string[];
  filas: (string | number | null)[][];
}

export const marcasApi = {
  detalle: (p: {
    marca?: string; desde?: string; hasta?: string; region?: string; estado?: string;
    ciudad?: string; vendedor?: string; grupo?: string; cliente?: string; sucursal?: string;
    categoria?: string; sku?: string; yoy?: string;
  }) => api.get<MarcaDetalle>('/marcas/detalle', p as Record<string, string | undefined>),
  cuadro: (p: {
    /** La ventana como `AAAAMM,AAAAMM,…` — `meta.periodos` de /marcas/detalle. */
    marca?: string; periodos: string; dims: string;
    region?: string; estado?: string; ciudad?: string; vendedor?: string; grupo?: string;
    cliente?: string; sucursal?: string; categoria?: string; sku?: string;
    /** Desde qué fila del agregado; se omite para el primer bocado. */
    desde?: string;
    /** `'1'` = sumar también el importe en USD; se omite si no está a la vista. */
    importe?: string;
  }) => api.get<MarcaCuadro>('/marcas/cuadro', p as Record<string, string | undefined>),
};

// ─────────────────────────── Módulo Clientes ───────────────────────────
// Las 5 páginas comparten meta, filtros y el bloque de KPIs; cada una añade lo
// suyo. Réplica del v88, paridad en 04_DOCS/PARIDAD_Clientes.md.

export interface ClientesMeta {
  pagina: string;
  empresa: 'GENERAL' | 'DDH' | 'DHV' | 'DIFRANCA';
  metrica: 'cajas';
  anio: number;
  /** Los NÚMEROS de mes de la ventana. El v88 no usa un rango continuo. */
  meses: number[];
  desde: string; hasta: string; rango: { min: string; max: string };
  empresas: number[];
  filtros: {
    region: number | null; vendedor: string | null;
    grupo: string | null; cliente: string | null;
  };
  /** true = el corte mezcla cajas (DDH/DHV) con unidades (Difranca). */
  mezclaUnidades: boolean;
}

export interface ClientesFiltros {
  regiones: { id: number; nombre: string }[];
  vendedores: string[];
  /**
   * Las dos mitades de la dimensión grupo: las CADENAS (`cadena: true`,
   * etiqueta = su nombre) y los clientes SUELTOS (`cadena: false`, etiqueta
   * «COD — NOMBRE»), que en `_RAW` son su propio grupo. El `valor` es lo que
   * viaja en el parámetro `grupo`: el nombre de la cadena o el código.
   */
  grupos: { valor: string; etiqueta: string; cadena: boolean }[];
  clientes: { cod: string; nombre: string }[];
}

interface Base { meta: ClientesMeta; filtros: ClientesFiltros }
export type Valor = { etiqueta: string; v: number };

export interface ClientesGeneral extends Base {
  kpis: {
    total: number; clientes: number; grupos: number; porCliente: number;
    prevMesTotal: number | null; prevAnioTotal: number | null;
    prevMesClientes: number | null; prevAnioClientes: number | null;
  };
  serie: { mes: number; v: number }[];
  seriePrev: { mes: number; v: number }[] | null;
  totalPrev: number | null;
  clientesPrev: number | null;
  topClientes: Valor[];
  porEmpresa: Valor[];
  porRegion: Valor[];
}

export interface ClientesMaestra extends Base {
  kpis: {
    clientes: number; gruposComerciales: number; individuales: number;
    total: number; mesesPromedio: number;
  };
  clientesPorRegion: Valor[];
  tipo: { enGrupo: number; individual: number };
  tabla: {
    cod: string; nombre: string; empresa: string; region: string;
    vendedor: string; grupo: string | null; v: number; meses: number;
  }[];
}

export interface ClientesActivacion extends Base {
  kpis: {
    activados: number; inactivados: number; neto: number; clientes: number;
    hayMesPrevio: boolean;
  };
  serieMes: { mes: string; activados: number; inactivados: number }[];
  activaciones: { mes: string; cod: string; nombre: string; region: string; v: number }[];
  inactivaciones: { mes: string; cod: string; nombre: string; region: string; v: number }[];
  /**
   * EL CUADRE DEL LIBRO `Acti_Inacti_Clientes_.xlsx`, al lado del cálculo de la
   * página (2026-09-02). Las dos cifras conviven porque **no miden lo mismo**:
   * `kpis.activados` son EVENTOS de transición mes a mes sobre la facturación, y
   * `libro.cuadres[].activados` es la FOTO de la cartera que cuadra C.E.I.
   * Ver la cabecera de esta rama en `netlify/functions/clientes-detalle.mts`.
   */
  libro: {
    /** `false` = no hay ni un cuadre cargado. Con `true` y `cuadres` vacío, el
     * libro existe pero no cubre los meses a la vista — son cosas distintas. */
    hay: boolean;
    cuadres: {
      empresa: string; empresa_id: number; mes: string;
      /** `Cajas` o `Unidades`, VERBATIM del encabezado de la hoja. */
      medida: string | null;
      /** El subtítulo del libro, con su cuadre y su fecha de C.E.I. */
      subtitulo: string | null;
      clientes: number; activados: number; inactivos: number;
      cantidad: number; importe: number;
    }[];
    /** El status del libro por cliente, para las dos tablas. Un mismo código
     * puede traer DOS filas, una por empresa (gotcha 73). */
    status: { cod: string; mes: string; empresa: string; status: string }[];
    /** Rótulos que el libro trae y que NO caen en la ventana de meses. */
    fueraDeVentana: string[];
    /** Cajas y unidades a la vista: el total de cantidad NO se suma. */
    mezclaMedidas: boolean;
    /** El filtro `grupo` está puesto y el libro no tiene esa columna. */
    sinFiltroGrupo: boolean;
    aviso: string | null;
    vacio: boolean;
  };
}

export interface ClientesTendencia extends Base {
  kpis: {
    total: number; clientes: number; pendiente: number; mejorMes: number | null;
    prevMesTotal: number | null; prevAnioTotal: number | null;
  };
  serie: { mes: number; v: number }[];
  seriePrev: { mes: number; v: number }[] | null;
  mitades: { h1: number; h2: number; n1: number; n2: number };
  tabla: {
    cod: string; nombre: string; total: number; h1: number; h2: number;
    variacion: number; tendencia: 'creciente' | 'estable' | 'decreciente';
  }[];
}

export interface ParamsClientes {
  empresa?: string; desde?: string; hasta?: string;
  region?: string; vendedor?: string; grupo?: string; cliente?: string;
  /** Texto libre (solo la Maestra): código o nombre de cliente, o grupo. */
  buscar?: string;
  yoy?: string;
}

const pedirClientes = <T>(pagina: string, p: ParamsClientes) =>
  api.get<T>('/clientes/detalle',
    { pagina, ...p } as Record<string, string | undefined>);

export const clientesApi = {
  general: (p: ParamsClientes) => pedirClientes<ClientesGeneral>('clientes', p),
  analisis: (p: ParamsClientes) => pedirClientes<ClientesGeneral>('cli-analisis', p),
  maestra: (p: ParamsClientes) => pedirClientes<ClientesMaestra>('cli-maestra', p),
  activacion: (p: ParamsClientes) => pedirClientes<ClientesActivacion>('cli-activacion', p),
  tendencia: (p: ParamsClientes) => pedirClientes<ClientesTendencia>('cli-tendencia', p),
};

export const ventasApi = {
  mensual: (p: {
    desde?: string; hasta?: string;
    metrica?: 'cajas' | 'unidades' | 'importe_usd';
    dim?: 'ninguna' | 'empresa' | 'region' | 'vendedor' | 'marca';
    empresa?: number; region?: number; vendedor?: string; marca?: number;
  }) => api.get<{ meta: Record<string, unknown>; filas: FilaVentaMensual[] }>(
    '/ventas/mensual', p as Record<string, string | number | undefined>,
  ),
};

/* ─────────── Módulo Financiero — las 7 vistas de una sola ruta ───────────
 * Paridad en 04_DOCS/PARIDAD_Financiero.md (7.170 celdas, 0 materiales).
 * Es el ÚNICO módulo del portal donde el USD es confiable; el resto es
 * cajas-only (CLAUDE.md §3).
 */
export type VistaFin =
  | 'resumen' | 'tendencia' | 'empresa' | 'marca' | 'skus' | 'clientes' | 'preciovol';

export interface FinDim { id: number; nombre: string; grupo?: string | null; codigo?: string }

export interface FinMeta {
  vista: VistaFin;
  desde: string; hasta: string;
  previo: { desde: string; hasta: string };
  segmento: 'all' | 'capilar' | 'electrico';
  empresas: number[];
  cobertura: { desde: string; hasta: string };
  fiscal: ReadonlyArray<{ anio: number; desde: string }>;
  moneda: string;
  /** Diferencias declaradas con el v88: la página las muestra como notas. */
  diferencias: string[];
  marcas: FinDim[]; regiones: FinDim[]; empresasDim: FinDim[];
  /** Solo en las vistas que lo declaran: 'calendario' | 'fiscal'. */
  calendario?: 'calendario' | 'fiscal';
}

export interface FinResumen {
  meta: FinMeta;
  kpis: {
    venta: number; cajas: number; unidades: number; precio_caja: number; ticket: number;
    clientes: number; skus: number; facturas: number;
    deltas: Record<string, number | null>;
    previo: Record<string, number>;
  };
  explorador: { venta: number; cajas: number; unidades: number; lineas: number };
  porAnioFiscal: { anio: number; venta: number; cajas: number; unidades: number }[];
  porEmpresa: { empresa_id: number; venta: number; cajas: number; unidades: number }[];
  porMarca: { marca_id: number | null; venta: number }[];
}

export interface FinTendencia {
  meta: FinMeta;
  mensual: { ym: number; anio: number; mes: number; venta: number; cajas: number }[];
  diaria: { fecha: string; venta: number }[];
  heatmap: { anio: number; meses: number[] }[];
  pico: { ym: number; venta: number } | null;
}

export interface FinEmpresa {
  meta: FinMeta;
  participacion: { empresa_id: number; venta: number; cajas: number; unidades: number }[];
  evolucion: { empresa_id: number; anios: { anio: number; venta: number }[] }[];
}

export interface FinMarca {
  meta: FinMeta;
  mix: { marca_id: number | null; venta: number }[];
  evolucion: { marca_id: number | null; anios: { anio: number; venta: number }[] }[];
}

export interface FinPrecioVol {
  meta: FinMeta;
  porAnio: { anio: number; venta: number; cajas: number; precio: number }[];
  porMarca: { marca_id: number; anios: { anio: number; precio: number; venta: number; cajas: number }[] }[];
  puente: {
    desde: number; hasta: number; venta_desde: number; venta_hasta: number;
    efecto_volumen: number; efecto_precio: number;
  } | null;
}

export interface FinSkus {
  meta: FinMeta;
  kpis: { activos: number; claseA: number; colaLarga: number; total: number; topParte: number };
  filas: {
    puesto: number; cod_art: string; descripcion: string | null; marca_id: number | null;
    venta: number; parte: number; acumulado: number; clase: 'A' | 'B' | 'C';
    porAnio: { anio: number; venta: number }[]; delta: number | null;
  }[];
  foco: {
    cod_art: string; descripcion: string | null; marca_id: number | null;
    venta: number; puesto: number | null; de: number; parte: number;
    delta: number | null; mesesConVenta: number;
    porMes: { ym: number; venta: number }[];
    porEmpresa: { empresa_id: number; venta: number }[];
    porRegion: { region_id: number; venta: number }[];
  } | null;
}

export interface FinClientes {
  meta: FinMeta;
  kpis: {
    activos: number; top10Parte: number; clientes80: number;
    churn: number; basePrevia: number; churnTasa: number; total: number;
  };
  filas: {
    puesto: number; cod_cli: string; nombre: string; venta: number;
    meses: number; recencia: number; parte: number; acumulado: number;
  }[];
  rfm: Record<string, number>;
  nuevosVsRecurrentes: { anio: number; nuevos: number; recurrentes: number }[];
  cohortes: {
    cohorte: number; tamano: number;
    retencion: ({ off: number; ym: number; clientes: number; tasa: number } | null)[];
  }[];
}

export interface ParamsFin {
  desde?: string; hasta?: string;
  empresas?: string; regiones?: string; marcas?: string;
  segmento?: 'all' | 'capilar' | 'electrico';
  sku?: string;
}

const pedirFin = <T>(vista: VistaFin, p: ParamsFin) =>
  api.get<T>('/financiero/detalle', { vista, ...p } as Record<string, string | undefined>);

export const financieroApi = {
  resumen: (p: ParamsFin) => pedirFin<FinResumen>('resumen', p),
  tendencia: (p: ParamsFin) => pedirFin<FinTendencia>('tendencia', p),
  empresa: (p: ParamsFin) => pedirFin<FinEmpresa>('empresa', p),
  marca: (p: ParamsFin) => pedirFin<FinMarca>('marca', p),
  skus: (p: ParamsFin) => pedirFin<FinSkus>('skus', p),
  clientes: (p: ParamsFin) => pedirFin<FinClientes>('clientes', p),
  preciovol: (p: ParamsFin) => pedirFin<FinPrecioVol>('preciovol', p),
};

/* ─────────── Módulo Metas ───────────
 * RETIRADO el 2026-09-03. La sección dejó de ser el «meta vs real por
 * vendedor/mes/región» del v88 y pasó a ser la FICHA DE METAS del mes
 * (db/126_metas_ficha.sql): la usuaria eligió «se retira: solo las fichas».
 * Su cliente vive ahora en `src/lib/api-metas-ficha.ts` y su endpoint en
 * `netlify/functions/metas-ficha.mts`; `metas-detalle.mts` y los tipos
 * `GrupoMeta`/`MetasDetalle`/`ParamsMetas` se fueron con la página vieja.
 *
 * `core.meta_vendedor` NO se tocó: la sigue leyendo el Reporte Flash, y con
 * ella sigue vivo `npm run etl:metas`. El gate de paridad de la página vieja
 * (04_DOCS/PARIDAD_Metas.md, 1.152 celdas) se conserva como historia.
 */

/* ─────────── Módulo Power Query / Consulta general ───────────
 * RETIRADO el 2026-10-08. La sección dejó de ser el explorador y las vistas
 * fijas sobre `_PQC`/`_PQF` y pasó a ser el «Dashboard DDH» que trajo la
 * usuaria (db/228): «esta seccion ahora va a ser sustituida por la maqueta».
 * Su cliente vive en `src/lib/api-pq-tablero.ts` y su endpoint en
 * `netlify/functions/pquery-tablero.mts`. Los tipos `DimPq`/`PqPivote`/
 * `PqPanelFijo`/`ParamsPq` se fueron con la página vieja.
 *
 * `pquery-detalle.mts` NO se borró: lo sigue sirviendo el conector de Claude
 * del gerente (`_lib/mcp-secciones.ts`), y con él `npm run e2e:pquery`. Las
 * tablas `core.pq_consulta`/`pq_panel`/`pq_libro` siguen en la base.
 */

/* ─────────── Módulo Analítica — la suite PM20, 6 páginas ───────────
 * Paridad en 04_DOCS/PARIDAD_ANALITICA.md (28.583 celdas, 0 materiales) y el
 * endpoint verificado por HTTP contra el propio arnés (`npm run e2e:analitica`,
 * 32.775 comprobaciones).
 *
 * Todo va en CAJAS, y para Difranca esa columna son UNIDADES — igual que en el
 * v88, en `_RAW` y en el Resumen.
 *
 * Tres cosas que hay que respetar al pintar, porque no son cosméticas:
 *  · `meta.diferencias` y `meta.avisos` se PINTAN. Ahí viaja la D14 (el What-If
 *    no tiene paridad con el monolito a propósito), el cambio en el número de
 *    filas del eje artículo, y el año anterior vacío cuando se elige el primer
 *    año de la ventana. La usuaria compara con el v88: enterarse por una cifra
 *    que no cuadra es la peor manera de descubrirlo.
 *  · La VENTANA son los dos últimos años presentes (2025-2026), que es lo que
 *    cubre `_RAW`. Los umbrales se indexan sobre esa lista de períodos.
 *  · A igualdad de valor el orden es por CLAVE ascendente. El v88 desempata por
 *    el índice interno de su cubo y hay 9 empates que cruzan un corte ABC.
 */
export type PaginaAn = 'insights' | 'comparador' | 'pareto' | 'puente' | 'heatmap' | 'whatif';
export type AmbitoAn = '' | 'DDH' | 'DHV' | 'DIFRANCA';

export interface AnMeta {
  pagina: PaginaAn;
  ambito: AmbitoAn;
  metrica: 'cajas';
  ventana: { anios: number[]; periodos: number; desde: string; hasta: string };
  ultimo: { anio: number; mes: number };
  empresas: number[];
  anio: number;
  aniosDisponibles: number[];
  diferencias: string[];
  avisos: string[];
}

/** Una tarjeta de Insights. `k` es la clave con la que el v88 ordena y desempata. */
export interface AnTarjeta {
  sev: 1 | 2 | 3;
  tag: string;
  k: string;
  dim?: string;
  ent?: string;
  x: number;
  mu?: number;
  z?: number;
  yoy?: number | null;
  v: number;
  /**
   * `true` = la cifra de la tarjeta es del GRUPO y no del alcance de quien la
   * mira. Solo lo llevan las del eje MARCA, y solo para usuarios recortados
   * (MARCA_NACIONAL en `analitica-detalle.mts`). La página lo rotula: una cifra
   * de otro universo tiene que decir de cuál.
   */
  nac?: boolean;
}

export interface AnInsights {
  meta: AnMeta;
  /**
   * `recortado` = el usuario no lo ve todo (asesor, coordinador, candado de
   * empresa). `marcaNacional` = por eso sus tarjetas de marca van en cifras
   * del grupo. Hoy valen lo mismo; son dos campos porque son dos preguntas.
   */
  alcance: { recortado: boolean; marcaNacional: boolean };
  tarjetas: AnTarjeta[];
  minPeriodos: boolean;
  cartera: {
    nCaida: number; nSilencio: number; top5: number; tot: number; shr: number;
    actNow: number; actYoY: number; dCov: number | null; mesTxt: string;
  } | null;
  caida: { k: string; tot: number; pct: number; nombre: string }[];
  silencio: { k: string; tot: number; mesesSin: number; nombre: string }[];
  top5: { k: string; v: number; nombre: string }[];
}

export interface AnTop { k: string; etiqueta: string; v: number }

export interface AnColumna {
  ent: string;
  /** 12 posiciones por número de mes, no por período. */
  cur: number[]; prev: number[];
  totC: number; totP: number; dPct: number | null;
  topMar: AnTop[]; topCli: AnTop[]; topArt: AnTop[];
  nCli: number; nArt: number; nMeses: number;
  /** Divide por los meses CON VENTA, no por 12. Es lo que hace el v88. */
  promMes: number;
}

export interface AnComparador {
  meta: AnMeta & { tipo: 'vend' | 'marca' | 'cliente'; entidades: string[] };
  /** Con el tipo cliente `etiqueta` trae el nombre; en los otros dos es la clave. */
  catalogo: { k: string; v: number; etiqueta: string }[];
  columnas: AnColumna[];
}

/** Un vendedor del catálogo del Pareto, con las zonas en las que factura. */
export interface AnVendedorCat {
  cod: string;
  /** `core.vendedor.nombre`, que para varios códigos está vacío. */
  nombre: string | null;
  /** Ids de `core.region` donde tiene movimiento: 102 y 816 tienen más de una. */
  zonas: number[];
}

export interface AnPareto {
  meta: AnMeta & {
    dim: 'cliente' | 'articulo';
    /* El recorte APLICADO, como lo devuelve el servidor. La página lee de aquí
     * y no de su propio estado, igual que hace ya con `dim`, `anio` y `ambito`:
     * quien manda sobre lo que se pintó es la respuesta. */
    zona: number | null; zonaNombre: string | null;
    ven: string | null; venNombre: string | null;
    /** Solo lo que TIENE movimiento en la ventana y dentro del alcance. */
    catalogos: {
      zonas: { id: number; nombre: string }[];
      vendedores: AnVendedorCat[];
    };
  };
  pareto: {
    grand: number; nA: number; nB: number; nC: number; entidades: number;
    filas: {
      puesto: number; k: string; etiqueta: string; v: number;
      parte: number; acum: number; clase: 'A' | 'B' | 'C';
    }[];
  };
  rfm: {
    /** Pueden venir en `null`: con menos de 5 clientes el corte no existe. */
    cortes: (number | null)[];
    nActivos24: number;
    segmentos: { seg: string; n: number; caj: number }[];
    filas: {
      c: string; etiqueta: string; R: number; F: number; M: number;
      rM: number; f: number; caj: number; seg: string;
    }[];
  };
}

export interface AnBarra { k: string; d: number; cur: number; prv: number }

export interface AnPuente {
  meta: AnMeta & { dim: 'marca' | 'region' | 'empresa' | 'categoria'; m1: number };
  puente: {
    deltas: AnBarra[]; t0: number; t1: number; m1: number; entidades: number;
    variacion: number; variacionPct: number;
    mayorAporte: AnBarra | null; mayorResta: AnBarra | null;
  };
}

export interface AnHeatmap {
  meta: AnMeta & { eje: 'marca' | 'empresa' | 'region' };
  filas: { k: string; tot: number; fmax: number; porAnio: { anio: number; meses: number[] }[] }[];
  anios: number[];
  gmax: number;
}

export interface AnWhatIfItem {
  label: string; marcas: string[];
  base: number; base6: number[]; factor: number;
  h24: number; h25: number; h26: number; ytd: number; h2Prev: number;
  real6: number[];
}

export interface AnWhatIf {
  meta: AnMeta & { anio24: number };
  whatif: {
    items: AnWhatIfItem[];
    total: number; ytd: number; h2Prev: number; totPrev: number; anual: number;
    dH2: number | null; dAnual: number | null;
  };
}

export interface ParamsAn {
  emp?: AmbitoAn; anio?: number;
  dim?: 'cliente' | 'articulo';
  dimp?: 'marca' | 'region' | 'empresa' | 'categoria';
  eje?: 'marca' | 'empresa' | 'region';
  tipo?: 'vend' | 'marca' | 'cliente';
  a?: string; b?: string;
  /** Pareto: el recorte por zona (`core.region.id`) y por código de vendedor. */
  zona?: number; ven?: string;
}

const pedirAn = <T,>(pagina: PaginaAn, p: ParamsAn) =>
  api.get<T>('/analitica/detalle',
    { pagina, ...p } as Record<string, string | number | undefined>);

export const analiticaApi = {
  insights: (p: ParamsAn) => pedirAn<AnInsights>('insights', p),
  comparador: (p: ParamsAn) => pedirAn<AnComparador>('comparador', p),
  pareto: (p: ParamsAn) => pedirAn<AnPareto>('pareto', p),
  puente: (p: ParamsAn) => pedirAn<AnPuente>('puente', p),
  heatmap: (p: ParamsAn) => pedirAn<AnHeatmap>('heatmap', p),
  whatif: (p: ParamsAn) => pedirAn<AnWhatIf>('whatif', p),
};

// ─────────── Cartera (Cobranzas) ───────────
// Los 5 reportes de CARTMOD. Las filas son ARREGLOS de valores en el orden de
// `columnas`, igual que en el blob del v88: el historial son 11.676 filas y
// repetir las claves en cada una triplicaría el JSON. Paridad demostrada en
// 04_DOCS/PARIDAD_CARTERA.md (677.843 celdas, 0 materiales).

export type RepCartera = 'clientes' | 'historial' | 'cxc' | 'diascalle' | 'cerradas';

export interface ColCartera {
  nombre: string;
  /** Cómo lo pinta el v88: money = fmtUSD · pct = v+'%' · status = pastilla. */
  tipo: 'txt' | 'money' | 'pct' | 'status';
}

export interface ParCat { clave: string; valor: unknown }

export interface CarteraReporte {
  meta: {
    reporte: RepCartera; titulo: string; desc: string;
    generado: string; corte: string;
    aviso_iva: string; aviso_duplicados?: string;
    orden_defecto: string; paginacion: string;
    alcance_no_aplicable?: string[];
    filtros: { emp: string; ven: string; q: string; mes: string };
    meses?: readonly string[];
  };
  columnas: ColCartera[];
  filas: (string | number | null)[][];
  total: number;
  sumas: { columna: string; total: number }[];
  duplicados: { filas: number; monto: number } | null;
  empresas: ParCat[];
  zonas: ParCat[];
  vendedores: ParCat[];
}

export interface ParamsCartera {
  emp?: string; ven?: string; q?: string; mes?: string;
}

export const carteraApi = {
  reporte: (rep: RepCartera, p: ParamsCartera = {}) =>
    api.get<CarteraReporte>('/cartera/reporte', {
      rep,
      ...(p.emp && p.emp !== '__all' ? { emp: p.emp } : {}),
      ...(p.ven && p.ven !== '__all' ? { ven: p.ven } : {}),
      ...(p.q ? { q: p.q } : {}),
      ...(p.mes && p.mes !== '__all' ? { mes: p.mes } : {}),
    }),
};

/* ─────────── Bitácora FTD (`bitftd`) ───────────
 * La pestaña de CONSULTA de FTDMOD. Paridad en 04_DOCS/PARIDAD_BITFTD.md
 * (200.947 celdas en 337 escenarios, 0 materiales) y el endpoint verificado por
 * HTTP contra el propio arnés (`npm run e2e:bitftd`).
 *
 * Tres cosas que hay que respetar al pintar, porque no son cosméticas:
 *  · **Los 4 KPIs NO responden a los filtros.** `kpis()` del v88 llama a `all()`,
 *    no a `filtered()`. El único número que se mueve es el conteo de filas.
 *  · **`kpis.mes` cuenta el MES EN CURSO** y el archivo tiene su corte el
 *    21/07/2026, así que hoy vale 0. Va con su aviso al lado o parece un fallo
 *    de carga (gotcha 51).
 *  · **El desplegable de marca trae 11 etiquetas para 6 marcas reales.** Se
 *    pintan las 11: son la interfaz del monolito (gotcha 52).
 *
 * Las filas vienen como OBJETOS con los 18 campos, no como los 14 valores que la
 * tabla pinta, porque el modal de detalle muestra los 18 y el CSV saca 19.
 */
export interface FilaBitftd {
  fila_origen: number;
  id_bitacora: string | null;
  f: string;
  cod_sucursal: string | null;
  sucursal: string;
  pi: string; pf: string;
  tipo_actividad: string;
  evento: string;
  marca_txt: string;
  sku_txt: string | null;
  objetivo: string;
  resultados: string | null;
  obstaculos: string | null;
  acciones: string | null;
  resp_seguimiento: string;
  registrado_por: string;
  dc: number | null;
  co: number | null;
}

export interface ColBitftd {
  nombre: string;
  tipo: 'txt' | 'fecha' | 'pct' | 'money' | 'origen';
}

export interface KpisBitftd {
  total: number; mes: number; sucursales: number; portal: number;
}

export interface BitftdRespuesta {
  meta: {
    modulo: 'bitftd'; titulo: string; subtitulo: string; desc?: string;
    corte: string | null; desde: string | null;
    orden_defecto?: string; paginacion?: string;
    alcance_no_aplicable?: string[];
    aviso_alcance: string; aviso_mes: string; aviso_marcas: string;
    filtros?: { ma: string; ta: string; su: string; d0: string; d1: string; q: string };
    /** db/250: si este usuario puede cargar el libro, y el sello de la última carga. */
    puede_cargar?: boolean;
    ultima_carga?: UltimaCargaBitftd | null;
  };
  kpis: KpisBitftd;
  columnas: ColBitftd[];
  filas?: FilaBitftd[];
  total?: number;
  costo_mostrado?: { valor: number; nota: string };
  marcas: string[];
  tipos: string[];
  sucursales: string[];
}

export interface ParamsBitftd {
  ma?: string; ta?: string; su?: string; d0?: string; d1?: string; q?: string;
}

export interface UltimaCargaBitftd {
  archivo: string; filas: number; rechazadas: number; fecha_hasta: string | null;
  cargado_por: string; cargado_en: string;
}

/** Lo que devuelve `POST /bitacora-ftd?seccion=carga` en sus dos pasos (db/250). */
export interface CargaBitftdRespuesta {
  ok: true;
  confirmado: boolean;
  analisis: {
    archivo: string; filas: number; duplicadas: number;
    desde: string | null; hasta: string | null; costo: number;
    antes: { n: number; desde: string | null; hasta: string | null; costo: number } | null;
    avisos: string[];
  };
}

export const bitftdApi = {
  consulta: (p: ParamsBitftd = {}) =>
    api.get<BitftdRespuesta>('/bitacora-ftd', {
      ...(p.ma && p.ma !== '__all' ? { ma: p.ma } : {}),
      ...(p.ta && p.ta !== '__all' ? { ta: p.ta } : {}),
      ...(p.su && p.su !== '__all' ? { su: p.su } : {}),
      ...(p.d0 ? { d0: p.d0 } : {}),
      ...(p.d1 ? { d1: p.d1 } : {}),
      ...(p.q ? { q: p.q } : {}),
    }),
  /** Carga del libro (db/250). Van FILAS ya leídas por `bitftd-xls.ts`, no el archivo. */
  cargar: (cuerpo: { archivo: string; filas: unknown[]; rechazadas: number; confirmar: boolean }) =>
    api.post<CargaBitftdRespuesta>('/bitacora-ftd?seccion=carga', cuerpo),
};

/* ─────────────────────── Trade Marketing (`trade`) ───────────────────────
 * El reporte viene del literal `_TMK` del v88 y es un extracto EXTERNO ya
 * agregado — no se deriva de la base de ventas y no se recalcula (como `_PQC`).
 * Las tuplas van con la MISMA posición que en el blob: renombrarlas a objetos
 * en el cliente invitaría a cambiar el orden, y el orden es el de la fuente.
 * Ver netlify/functions/trade-detalle.mts y db/013_trade.sql.
 */

/** [mes, n, clientes, cajas, Δ% cajas | null, inversión USD] */
export type TmkMensual = [string, number, number, number, number | null, number];
/** [zona, cajas, % del total, n, clientes] */
export type TmkZona = [string, number, number, number, number];
/** [vendedor, zona, cajas, % del total, n] */
export type TmkVendedor = [string, string, number, number, number];
/** [etiqueta, cajas, % del total] — vale para `empresa`, `junZona` y `junEmp` */
export type TmkEtiqueta = [string, number, number];
/** [producto, cajas] */
export type TmkProducto = [string, number];
/** [cohorte, clientes, previa|null, mes negoc., posterior, Δ prev|null, Δ negoc., % al alza] */
export type TmkCohorte = [string, number, number | null, number, number, number | null, number, number];
/** [marca, línea, mecánica, canal, vigencia, fecha fin, tipo] */
export type TmkCalendario = [string, string, string, string, string, string, string];

export interface TmkVista {
  kpi: {
    promos?: number; escalas?: number;
    clientes: number; cajas: number; inv: number;
    junCajas: number; junVar: number;
    junPromos?: number; junEscalas?: number;
    junClientes: number; presentaciones: number;
  };
  mensual: TmkMensual[];
  zona: TmkZona[];
  vendedor: TmkVendedor[];
  empresa: TmkEtiqueta[];
  producto: TmkProducto[];
  junZona: TmkEtiqueta[];
  junEmp: TmkEtiqueta[];
  cohortes: TmkCohorte[];
  cohAgg: {
    n: number; previa: number; mesNeg: number; post: number;
    dPost: number; dPrev: number; up: number;
  };
}

export interface TradeRespuesta {
  meta: {
    fuente: string;
    aviso_universo: string;
    aviso_alcance: string;
    unidades: string;
  };
  promo: TmkVista;
  esc: TmkVista;
  cal: TmkCalendario[];
  narrativa: { promo: string; esc: string };
  presentaciones: {
    disponible: boolean;
    motivo: string;
    archivos: { tipo: string; mes: string; etiqueta: string; archivo: string; bytes: number; sha256: string }[];
  };
}

export const tradeApi = {
  detalle: () => api.get<TradeRespuesta>('/trade/detalle'),
};

/* ────────────── Negociaciones Comerciales (`nego`) ──────────────
 * LA PRIMERA PÁGINA DE ESCRITURA DEL PORTAL. Cuatro cosas que hay que respetar
 * al pintar, porque no son cosméticas:
 *
 *  · **Las cifras no se calculan aquí.** El saldo del presupuesto, el `v3` y el
 *    total los sirve y los REVALIDA el endpoint: la página los muestra y los usa
 *    para el semáforo, pero lo que se guarda es lo que recalcula el servidor. Un
 *    formulario que se cree sus propias cifras es «una cifra errada que corre sin
 *    error», solo que además queda guardada.
 *  · **Los textos de validación y los semáforos van verbatim** del v88, en su
 *    orden. Son los mismos que la usuaria lleva leyendo dos años — menos los dos
 *    que se cayeron con la regla nueva (ver `validar` en el endpoint).
 *  · **`meta.aviso_*` se PINTA.** Ahí viajan las diferencias declaradas: el
 *    registro es más restrictivo que el monolito (alcance en SQL), el botón de
 *    aprobar y el correo todavía no existen, y el mes que sirve de denominador.
 *  · **🔴 EL 3 % YA NO ES EL TECHO (2026-08-13).** Cada asesor tiene un monto
 *    asignado por trimestre y cada negociación se lo descuenta. `aporte_max` y
 *    `pct_aporte` NO EXISTEN: se eliminaron de la base en `db/027`.
 */
export type AmbitoNego = 'suc' | 'cli' | 'grp';

export interface NegoCatalogos {
  meta: {
    modulo: string; titulo: string; subtitulo: string;
    aviso_catalogos: string; aviso_vendedores: string;
    aviso_aprobacion: string; aviso_presupuesto: string;
    /** Qué bolsa paga cada marca. `null` = hay que preguntarlo (Multimarca). */
    bolsa_de_marca: Record<string, 'COSMETICOS' | 'GAMA' | null>;
    aviso_promo_b2b: string;
    es_master: boolean;
  };
  /** Repartidas por tipo, como en la maqueta de la sección (2026-08-19): la
   *  lista que se ofrece depende de si la negociación es de Ventas o de Trade.
   *  Dos estrategias viven en las dos listas a propósito. «Promo B2B» entró en
   *  Ventas el 2026-08-20 y abre el renglón de promos vigentes. */
  estrategias: { VENTAS: string[]; TRADE: string[] };
  marcas: string[];
  aplicaciones: string[];
  tipos: { valor: string; etiqueta: string }[];
  /** 17 de la base, no los 16 del monolito. 2 son plazas VACANTES, no personas
   *  (`vacante` sale del nombre, «Vacante…»: el 721 ya es José Sánchez).
   *  `solo_trade` marca a «Trade Marketing (sin código)» (2026-09-07): no es un
   *  vendedor, solo viaja para quien no tiene alcance de vendedor y el
   *  desplegable lo ofrece únicamente con el tipo TRADE. Ver `src/lib/nego-trade.ts`. */
  vendedores: { codigo: string; nombre: string | null; vacante: boolean; solo_trade?: boolean }[];
  /** Cuántas esperan aprobación por tipo, dentro del alcance del usuario
   *  (2026-09-07). La pestaña «Pendientes» lo pinta en su rótulo y decide con
   *  qué tipo abrirse. Opcional porque el servidor del DEMO no lo arma. */
  pendientes?: { VENTAS: number; TRADE: number };
  /** Promos B2B activas (db/043), con su vigencia: la página filtra por solape
   *  con el período tecleado y el servidor repite el filtro en el alta. Vacío
   *  hasta que se carguen — la usuaria dijo que se cargarán más adelante. */
  promos_b2b: {
    id: number; nombre: string; descripcion: string | null;
    fecha_inicio: string; fecha_fin: string;
  }[];
  /** Solo en Negociaciones BBK (db/190): los SKU VIGENTES —BBK facturados en los
   *  3 meses previos al corte—, calculados por el servidor en cada petición.
   *  `ultima_venta` es la última fecha con TX, para el rótulo del selector. */
  skus_bbk?: { codigo: string; descripcion: string | null; ultima_venta: string | null }[];
  /** Las opciones de «Especifique estrategia» (solo `vista=bbk`). */
  detalles_bbk?: readonly string[];
}

/** La pestaña «Acumulado BBK» (db/190): lo que suma la sección, que no tiene
 *  presupuesto. Suman las vivas —registradas, pendientes y aprobadas—; las
 *  rechazadas y las anuladas no. Con el alcance del usuario. */
export interface NegoAcumuladoBbk {
  /** `aviso_unidades` y los campos de unidades llegaron el 2026-10-01: opcionales
   *  para que una respuesta de un servidor anterior no rompa la pestaña. */
  meta: { modulo: string; vista: string; aviso: string; aviso_unidades?: string; aviso_alcance: string };
  total: {
    /** Σ de `por_sku`. */
    unidades?: number;
    negociaciones: number; usd: number;
    pendientes: number; usd_pendientes: number;
    aprobadas: number; usd_aprobadas: number;
    rechazadas: number; anuladas: number;
  };
  por_mes: { ym: number; etiqueta: string; negociaciones: number; usd: number }[];
  por_asesor: {
    vendedor_cod: string | null; vendedor_nombre: string | null;
    negociaciones: number; usd: number;
  }[];
  /** Unidades por SKU: las del reporte final si se confirmó, si no las del alta. */
  por_sku?: { codigo: string; descripcion: string | null; unidades: number; negociaciones: number }[];
  /** Las que suman monto pero no tienen cantidades (registradas antes de db/196). */
  sin_cantidades?: { codigo: string; skus: number }[];
}

export interface NegoSugerencias {
  meta: { alcance: AmbitoNego; q: string; restringido: boolean; vacio: string };
  /** `sin_fact`: ni una línea TX desde 2024. Desde el 2026-09-16 el buscador ya
   *  NO los esconde —se le puede montar una negociación a un cliente que aún no
   *  compra, que fue el pedido de la usuaria con CAR937 en la mano—: van al
   *  final de la lista y la pantalla los MARCA. La tarjeta que sale al elegirlo
   *  ya lo dice por su lado («sin histórico financiero USD», `con_serie`). */
  sugerencias: {
    clave: string; nombre: string; n: number; etiqueta: string; sin_fact: boolean;
  }[];
}

export interface NegoTarjeta {
  meta: {
    alcance: AmbitoNego; clave: string; alcance_txt: string;
    aviso_trimestre: string; aviso_vendedor: string; aviso_ult_fact: string;
  };
  cliente: { nombre: string; codigos: string[]; codigo: string; chip: string };
  tarjeta: {
    v3: number; promedio: number; meses: string;
    /** El denominador del %: el MES CALENDARIO ANTERIOR. `cargado` distingue
     *  «el cliente no compró» de «el portal aún no tiene ese mes». */
    ult_fact: { ym: number; etiqueta: string; usd: number; cargado: boolean };
    serie: { ym: number; etiqueta: string; v: number }[];
    region: string | null; region_id: number | null;
    vendedor: string | null; vendedor_nombre: string | null;
    nsuc: number; con_serie: boolean;
  };
  semaforo: { nivel: 'ok' | 'mid' | 'bad'; texto: string };
  /** Solo con `vista: 'bbk'` (db/196): el último precio de cada SKU vigente
   *  para ESTE cliente (o el general, si nunca se lo compró), por código. */
  precios_bbk?: Record<string, PrecioBbk>;
  empresa_bbk?: string | null;
  /** ¿Es Farmatodo? Solo entonces el menú ofrece «Descuento 70%» (2026-10-02). */
  farmatodo_bbk?: boolean;
  detalles_bbk?: string[];
  aviso_precios_bbk?: string;
}

/** Lo que el panel del REPORTE FINAL de una negociación BBK necesita (db/196). */
export interface NegoReporteBbk {
  meta: { modulo: string; vista: string; puede_confirmar: boolean; aviso: string };
  negociacion: {
    id: number; codigo: string | null; estado: string;
    cliente: string | null; cod_cli: string | null; empresa: string | null;
    estrategia_detalle: string | null; pct: number | null; monto_otorgado: number;
    /** Las líneas del alta. [] en una BBK anterior a db/196. */
    lineas: LineaBbk[]; total: number | null;
    /** NULL mientras no se confirme. */
    reporte: {
      lineas: LineaBbk[]; total: number; monto: number; por: string | null; en: string | null;
    } | null;
  };
  /** Los SKU que el reporte puede usar: los vigentes más los de la negociación. */
  skus: SkuBbk[];
  precios: Record<string, PrecioBbk>;
}

export interface NegoReporteBbkRespuesta {
  ok: true;
  correo: { estado: 'ENVIADO' | 'ERROR' | 'SIN_TRANSPORTE'; destinatarios: string[]; avisos: string[] };
  reporte: { lineas: LineaBbk[]; total: number; monto: number; pct: number | null; por: string; en: string };
}

/** Una bolsa viva: lo asignado, lo consumido y lo que queda. */
export interface SaldoNego {
  id: number; vendedor_cod: string; zona: string;
  /** De quién es la bolsa (db/141): `VENTAS` es el reparto por asesor de
   *  db/027 y `TRADE` el de Trade Marketing, por zona y con `vendedor_cod`
   *  = `TMK`. Opcional porque un servidor anterior al 2026-09-08 no lo manda. */
  destino?: 'VENTAS' | 'TRADE';
  bolsa: string; cubeta: string;
  monto: number; consumido: number; disponible: number;
  umbral_pct: number; pct_consumido: number | null;
  /** db/202: de `consumido`, lo que descontaron las promociones aceptadas (el mismo presupuesto). Opcional: un
   *  servidor anterior al 2026-10-01 no lo manda. */
  consumido_promo?: number;
}

/**
 * El presupuesto de TRADE MARKETING del trimestre (db/141, 2026-09-08).
 *
 * Ocho bolsas —4 zonas × 2 empresas— del libro `PRESUPUESTO_TMK_negociaciones`,
 * hoja `PPTO TMK`. Va SEPARADO del de ventas en la misma pestaña y sus cifras no
 * entran en ningún total de Ventas: son dos presupuestos con dos dueños.
 */
export interface PresupuestoTrade {
  etiqueta: string; fuente: string;
  monto: number; consumido: number; disponible: number;
  /** Por empresa, como las dos tarjetas de Ventas. */
  bolsas: {
    bolsa: 'COSMETICOS' | 'GAMA'; etiqueta: string;
    monto: number; consumido: number; disponible: number;
    pct_consumido: number | null;
  }[];
  /** Por zona, que es la unidad del reparto en el libro de Trade. */
  zonas: {
    zona: string; etiqueta: string;
    cosmeticos: { monto: number; consumido: number; disponible: number } | null;
    gama: { monto: number; consumido: number; disponible: number } | null;
    monto: number; consumido: number; disponible: number;
    pct_consumido: number | null;
  }[];
  /** Las ocho bolsas, una por (zona, empresa). Van aquí y no en
   *  `NegoPresupuesto.filas`, que es el reparto de Ventas por asesor. */
  filas: SaldoNego[];
  /**
   * Lo que el redondeo al céntimo hace que las partes NO sumen el total, y que
   * la pestaña DICE en vez de repartir.
   *
   * El libro de Trade reparte con CINCO decimales (7.083,99825) y casi todas
   * las partes redondean hacia arriba, así que las cuatro zonas suman
   * `partes` = 40.480,00 donde el total del libro es 40.479,99: `dif` = 0,01.
   * Se probó quitarle el céntimo al mayor y se descartó — habría dejado
   * Andes-Occidente · Cosméticos en 8.500,79 donde el libro tiene 8.500,7979,
   * o sea cambiando un dato del libro para que cuadrara una suma.
   *
   * `null` cuando cuadra, que es lo normal.
   */
  residuo: { partes: number; dif: number } | null;
  /** Lo comprometido ANTES de que existiera el presupuesto y que por decisión
   *  de db/141 NO se le descuenta. `null` cuando no hay ninguna. */
  sin_bolsa: { n: number; usd: number } | null;
  aviso: string; aviso_aprobacion: string;
}

export interface NegoPresupuesto {
  meta: {
    modulo: string; vista: string; anio: number; trimestre: number;
    etiqueta: string; aviso: string; aviso_bolsas: string;
    aviso_reasignacion: string; vacio: string;
    /** Asesores de MI zona: los únicos a los que se les puede cargar el gasto.
     *  Vacío en un trimestre cerrado: no hay a qué reasignar. */
    reasignables: string[];
    /** El filtro de trimestres anteriores (2026-10-01). Opcionales porque un
     *  servidor anterior no los manda: sin ellos la pestaña no pinta el filtro. */
    en_curso?: boolean;
    trimestres?: OpcionTrimestre[];
    /** El aviso de «trimestre cerrado»; `null` en el en curso. */
    aviso_cerrado?: string | null;
  };
  /** Los DOS presupuestos separados (usuaria, 2026-08-20): DDH+DHV (Cosméticos)
   *  y Difranca (GA.MA). La cubeta FARMATODO del 103 NO está en estos totales:
   *  se administra en NC Farmatodo. */
  bolsas: {
    bolsa: 'COSMETICOS' | 'GAMA'; etiqueta: string; marcas: string;
    monto: number; consumido: number; disponible: number;
    pct_consumido: number | null;
    /** db/202: `consumido` partido por origen (suman `consumido`). Opcionales: servidor anterior. */
    negociaciones?: number; promociones?: number;
    /** La cubeta FARMATODO de ESTA bolsa, que la tarjeta dice debajo sin sumarla
     *  (2026-10-02), y el total con ella —la cifra del libro—. NULL para quien no
     *  la ve por alcance; opcionales: servidor anterior. */
    farmatodo?: number | null; con_farmatodo?: number | null;
  }[];
  /** La cubeta FARMATODO del 103 (las dos bolsas juntas), para la remisión a
   *  NC Farmatodo. NULL para quien no la ve por alcance. */
  farmatodo: { monto: number; consumido: number; disponible: number; promociones?: number } | null;
  /** El presupuesto de Trade Marketing (db/141). `null` mientras el trimestre no
   *  tenga filas cargadas, o para quien no ve ninguna zona. Opcional además
   *  porque un servidor anterior al 2026-09-08 no manda la rama. */
  trade?: PresupuestoTrade | null;
  filas: SaldoNego[];
  por_asesor: {
    vendedor_cod: string; zona: string; umbral_pct: number;
    monto: number; consumido: number; disponible: number;
    /** db/202 (la usuaria, 2026-10-01): el consumido partido — lo de las negociaciones (su detalle es el
     *  Registro) y el TOTAL de lo que descontaron las promociones. Suman `consumido`. Opcionales: servidor anterior. */
    negociaciones?: number; promociones?: number;
    pct_consumido: number | null; en_alerta: boolean; agotado: boolean;
    /** Las bolsas de ESE asesor que cruzaron su propio umbral o se agotaron. El
     *  umbral se mira bolsa por bolsa —es el `presupuesto_id` de la solicitud lo
     *  que la manda a aprobación—, nunca sobre la suma de la fila: el 411 tenía
     *  la bolsa GA.MA al 50,22 % con su fila al 28,0 % (medido 2026-08-24). Es
     *  lo que pone la fila en ámbar y lo que la tabla nombra bajo el %. */
    bolsas_alerta: {
      bolsa: string; cubeta: string;
      /** «GA.MA», «Cosméticos», o con su cubeta detrás: «Cosméticos · FARMATODO». */
      etiqueta: string;
      monto: number; consumido: number;
      pct_consumido: number | null; umbral_pct: number; agotada: boolean;
    }[];
    /** false cuando las bolsas del asesor NO comparten umbral: entonces
     *  `umbral_pct` es el de una de ellas y la barra no dibuja la marca, porque
     *  una sola marca sobre la suma no significaría nada. */
    umbral_uniforme: boolean;
  }[];
}

export interface FilaNego {
  id: number;
  /** N° de control `NEG-AAMMDD-VENDEDOR-NN` (D40). NULL en las 9 filas del lote
   *  histórico, que nunca tuvieron número: la tabla las pinta con «—». */
  codigo: string | null;
  fecha: string; tipo: string;
  /**
   * De qué MITAD del presupuesto sale el dinero (db/156): 'VENTAS' (el reparto
   * por asesor) o 'TRADE' (el de Trade Marketing por zona). Normalmente vale lo
   * mismo que `tipo`; cuando NO coincide, la fila sale en los DOS registros y la
   * celda de «Presupuesto» lo dice con palabras (`imputaCruzada` en
   * `lib/nego-bolsa.ts`). Hoy hay una sola: `NEG-260915-103-01`, de Trade y
   * pagada con el presupuesto comercial del 103.
   */
  imputa_destino: 'VENTAS' | 'TRADE';
  alcance: string;
  cod_cli: string | null; cliente_nombre: string | null;
  /**
   * La(s) empresa(s) del cliente (db/056): 'DDH', 'DHV', 'DIF' o combinadas
   * con '+' cuando el mismo código existe en varias — pasa en 410 de los 3.128
   * códigos de la maestra, así que la columna tiene que saber pintar dos.
   * NULL en las filas cuyo cliente no está en la maestra.
   */
  empresas: string | null;
  /** Cuántas evidencias tiene cargadas. La lista se pide aparte, al abrir. */
  n_evidencias: number;
  vendedor_cod: string | null; vendedor_nombre: string | null;
  region: string | null; estrategia: string | null;
  /** Nombre de la promo B2B verbatim al momento del alta; NULL si no aplica. */
  promo_b2b: string | null;
  marca_txt: string | null;
  aplicacion_desc: string | null; nro_factura: string | null; sucursales: number;
  fecha_inicio: string | null; fecha_fin: string | null;
  cantidad: number | null; monto_unitario: number | null;
  total: number; monto_otorgado: number | null; ventas_3m: number | null;
  ult_fact_ym: number | null; ult_fact_usd: number | null;
  /** NULL cuando el cliente no facturó el mes anterior: es INDEFINIDO, no 0. */
  pct_ult_fact: number | null;
  pct_consumo: number | null; supera_umbral: boolean;
  presupuesto_vendedor_cod: string | null; ppto_monto: number | null;
  /**
   * DE QUÉ BOLSA DEL TRIMESTRE SALE EL DINERO (pedido de la usuaria,
   * 2026-09-04): 'COSMETICOS' (DDH + DHV) o 'GAMA' (Difranca). Sale de
   * `presupuesto_nego.bolsa` por `presupuesto_id`, y es NULL cuando la
   * negociación no consume presupuesto (ver `SIN_BOLSA` en `lib/nego-bolsa.ts`).
   *
   * NO es lo mismo que `empresas`, que es un dato del CLIENTE: db/131 dejó
   * NEG-260823-411-01 en la empresa DDH con la bolsa GAMA. El porqué de cada
   * regla está en `lib/nego-bolsa.ts`.
   */
  ppto_bolsa: BolsaNego | null;
  /** La cubeta de esa bolsa ('FARMATODO' | 'RESTO_CADENAS'), solo en Key
   *  Accounts; '' en las demás y NULL cuando no hay bolsa. */
  ppto_cubeta: string | null;
  /**
   * De qué MITAD del presupuesto es esa bolsa (`presupuesto_nego.destino`):
   * 'VENTAS' (el reparto por asesor) o 'TRADE' (el de Trade Marketing por
   * zona). NULL cuando no hay bolsa. Va por el mismo JOIN que `ppto_bolsa` y no
   * se deduce de `tipo`: sin ella la celda decía «Cosméticos» igual para una de
   * Trade que para una de Ventas (la usuaria, 2026-09-23).
   */
  ppto_destino: 'VENTAS' | 'TRADE' | null;
  /** La zona de esa bolsa (`presupuesto_nego.zona`). En Trade es la unidad del
   *  reparto, así que es lo que se nombra en vez del asesor. */
  ppto_zona: string | null;
  estado: string; justificacion: string | null; creado_por: string;
  /** El Objetivo SMART (db/255). Solo las de TRADE desde el 2026-10-20; NULL en
   *  Ventas, en BBK y en las de Trade anteriores. Opcional en el tipo: las filas de
   *  prueba de los tests y un servidor anterior no lo traen. */
  objetivo_smart?: string | null;
  anulada_por: string | null; anulada_en: string | null; motivo_anulacion: string | null;
  /** El circuito (db/053). Los cuatro son NULL hasta que el coordinador decide.
   *  `dictamen` es el texto que se pinta; `motivo_decision` es lo que él escribió
   *  (obligatorio al rechazar, opcional al aprobar). */
  dictamen: string | null; motivo_decision: string | null;
  decidido_por: string | null; decidido_en: string | null;
  /** El VISTO BUENO del coordinador de una de Trade cargada por un asesor
   *  (db/256): quién la pasó a Trade Marketing, cuándo y su nota. NULL si no
   *  pasó por ese paso. Opcionales: un servidor anterior no los trae. */
  coord_decidido_por?: string | null; coord_decidido_en?: string | null;
  coord_motivo?: string | null;
  ym: number; etiqueta_mes: string;

  /* ── EL RETORNO DE LA INVERSIÓN (2026-08-25) ──────────────────────────────
   * «Retorno» aquí es VENTA INCREMENTAL SOBRE FACTURACIÓN: lo que el cliente
   * facturó durante la vigencia menos lo que ya venía comprando en el mismo
   * número de días justo antes, dividido entre el monto otorgado. NO es
   * ganancia — la base no tiene el costo de los productos — así que un `roi`
   * de 5,9 son 5,9 dólares FACTURADOS por cada dólar puesto, no 590 % de
   * utilidad. El porqué de cada regla está en el bloque «El retorno de la
   * inversión» de `netlify/functions/negociaciones.mts`. */

  /** La cifra. NULL siempre que `roi_estado` no sea 'curso' ni 'cerrada'. */
  roi: number | null;
  /** Venta incremental en dólares: `roi_venta - roi_base`. NULL con `roi`. */
  roi_incremento: number | null;
  /**
   * Por qué la cifra es la que es. La pantalla PINTA lo que esto diga y no lo
   * vuelve a deducir: la tabla y el Excel tienen que coincidir justo en los
   * casos raros, que son estos.
   *  · `anulada`       — devolvió su monto: no hay inversión que rentabilizar.
   *  · `sin_fechas`    — las 9 del lote histórico, sin vigencia que medir.
   *  · `sin_inversion` — monto otorgado nulo o cero.
   *  · `sin_empezar`   — la vigencia arranca después del corte de ventas.
   *  · `curso`         — parcial: la cifra se mueve y hay que enseñar los días.
   *  · `cerrada`       — la vigencia terminó dentro de los datos cargados.
   */
  roi_estado: 'anulada' | 'sin_fechas' | 'sin_empezar' | 'sin_inversion' | 'curso' | 'cerrada';
  /** El monto otorgado, o sea el divisor. Es `monto_otorgado` por otro nombre,
   *  repetido a propósito para que el detalle pueda enseñar la división entera
   *  sin que quien la lea tenga que saber que son la misma columna. */
  roi_inversion: number | null;
  /** Facturado en la vigencia hasta el corte. NULL si no se pudo medir. */
  roi_venta: number | null;
  /** Facturado en los mismos días JUSTO ANTES de la vigencia: la comparación. */
  roi_base: number | null;
  /** Días de vigencia ya transcurridos hasta el corte de ventas. */
  roi_dias_curso: number | null;
  /** Días que dura la vigencia completa. */
  roi_dias_total: number | null;
  /** Si la vigencia terminó dentro de los datos cargados. NULL sin fechas. */
  roi_cerrada: boolean | null;
  /** Hasta qué día llega la venta con la que se midió (el corte del ETL). */
  roi_corte: string | null;

  /* ── LA NOTA DE CRÉDITO (db/072, 2026-08-26) ──────────────────────────────
   * VIENEN SIEMPRE desde el 2026-09-03. Eran opcionales a propósito: el
   * endpoint solo los servía con `?vista=nc` para que el N° de NC no viajara al
   * navegador de los usuarios de `nego`, que db/072 pidió no modificar. La
   * usuaria pidió justo lo contrario —«el registro … en cualquier módulo se
   * debe ver como se ve en el módulo de finanzas»—, así que el dato es del
   * registro y no de una vista. `null` es «todavía sin NC» y ya no hay ningún
   * `undefined` que signifique «esta respuesta no la trae». */

  /** El N° del documento que emitió Facturación. */
  nro_nc: string | null;
  /** QUÉ documento es (db/129, 2026-09-03): la usuaria pidió que Facturación
   *  pueda cargar una nota de crédito **o una factura** y que elija cuál al
   *  cargarla. Por eso la columna se rotula «NC/FACT». Va SIEMPRE con
   *  `nro_nc`: los dos NULL o los dos puestos (negociacion_nc_traza_chk). */
  nc_tipo: TipoDocNego | null;
  /** Quién lo anotó. Va con `nro_nc` (CHECK negociacion_nc_traza_chk). */
  nc_cargado_por: string | null;
  /** 'AAAA-MM-DD HH:MM' del número VIGENTE: se reescribe al corregirlo. */
  nc_cargado_en: string | null;
  /**
   * EL MONTO QUE DICE LA NOTA DE CRÉDITO (db/170, la usuaria el 2026-09-21).
   * Cuando lo hay, `monto_otorgado` YA ES este número: el monto de la NC
   * sustituye al estimado del asesor y la diferencia se mueve en el presupuesto.
   * Así que esto NO es una segunda cifra que la pantalla tenga que preferir —lo
   * que se pinta sigue siendo `monto_otorgado`—: sirve para poder DECIR de dónde
   * salió ese monto. `null` = la negociación conserva el estimado.
   *
   * Solo existe con `nc_tipo === 'NC'`: con una factura no hay barra de monto
   * (decisión de la usuaria).
   */
  nc_monto: number | null;
  /** Lo que el asesor pidió, guardado el día que la NC lo sustituyó. `null` =
   *  nunca se sustituyó, y entonces el estimado ES `monto_otorgado`. Va con
   *  `nc_monto` (CHECK negociacion_estimado_traza_chk). */
  monto_estimado: number | null;
  /** Cuántos soportes de la NC tiene. La lista se pide aparte, al abrir. */
  n_nc_archivos: number;

  /* ── NEGOCIACIONES BBK (db/190) ───────────────────────────────────────────
   * Opcionales porque el DEMO y una pantalla vieja en caché no los traen. */
  /** 'NEGO' | 'BBK'. Cada registro enseña solo las suyas. */
  registro?: 'NEGO' | 'BBK';
  /** Los SKU negociados, con la descripción ENTERA; [] fuera de BBK. */
  skus?: { codigo: string; descripcion: string | null }[];
  /** El «Especifique estrategia». NULL fuera de BBK. */
  estrategia_detalle?: string | null;
  /* ── Cantidad y costo por SKU, y el reporte final (db/196). Solo en BBK. ── */
  bbk_lineas?: LineaBbk[] | null;
  bbk_total?: number | null;
  bbk_pct?: number | null;
  bbk_reporte?: LineaBbk[] | null;
  bbk_reporte_total?: number | null;
  bbk_reporte_monto?: number | null;
  bbk_reporte_por?: string | null;
  /** 'AAAA-MM-DD HH:MM', hora de Caracas. NULL = el reporte no se ha confirmado. */
  bbk_reporte_en?: string | null;
}

/** Los dos documentos con los que Facturación puede pagar una negociación
 *  (db/129). El texto que se pinta sale de `TIPO_DOC_NEGO`, no de un `switch`
 *  repetido en cada pantalla. */
export type TipoDocNego = 'NC' | 'FACTURA';

export const TIPO_DOC_NEGO: Record<TipoDocNego, { largo: string; corto: string }> = {
  NC: { largo: 'Nota de crédito', corto: 'NC' },
  FACTURA: { largo: 'Factura', corto: 'FACT' },
};

export interface NegoRegistro {
  meta: {
    modulo: string; vista: string; tipo: string; mes: string; q: string;
    etiqueta_tipo: string; es_master: boolean;
    /** Si esta respuesta TRAE las anuladas. Por defecto no: una negociación
     *  anulada se cargó mal, su monto volvió al presupuesto y sale del registro
     *  (la usuaria, 2026-08-24). */
    ver_anuladas: boolean;
    /** Si este usuario puede pedirlas (`anuladas=1`). Solo MASTER. Lo dice el
     *  servidor y no la página: los roles los tiene él. */
    puede_ver_anuladas: boolean;
    /** Qué pestaña del circuito se está viendo: '' (todo), 'pendientes' o
     *  'decididos'. Lo devuelve el servidor para que la página no dependa de su
     *  propio estado al pintar los textos. */
    filtro_estado: string;
    /** Si este usuario puede aprobar/rechazar las de ESTE tipo (D5): REGIONAL en
     *  Ventas, TRADEMARKETING en Trade, MASTER en las dos. Habilita los botones;
     *  el candado de verdad está en el endpoint, que además comprueba que no sea
     *  el mismo que registró la solicitud. */
    puede_decidir: boolean;
    /** Si puede decidir el PRIMER paso de una de Trade cargada por un asesor
     *  —las filas «Pendiente coordinador»— (db/256): el coordinador de la zona
     *  o un MASTER. `puede_decidir` vale para las demás. Opcional: un servidor
     *  anterior no lo trae, y entonces no hay botones en ese paso. */
    puede_decidir_coord?: boolean;
    /** Los códigos de vendedor cuyas filas puede decidir, cuando NO son todas
     *  las que ve: hoy solo KEYACCOUNTS, que lee el país y coordina el 714
     *  (db/244). `null` o ausente = los botones van en todas las filas que el
     *  tipo permita. El 403 `FUERA_DE_ZONA` del endpoint es el candado. */
    zona_decision?: string[] | null;
    /** Negociaciones BBK (db/196): si este usuario ve el botón «Reporte» y puede
     *  confirmarlo. Opcional: el demo y una respuesta vieja no lo traen. */
    puede_reportar_bbk?: boolean;
    /** Si esta respuesta TRAE las columnas de la NC. Desde el 2026-09-03 es
     *  SIEMPRE `true` —la NC se ve en los dos registros— y se conserva por
     *  compatibilidad: una pantalla vieja cacheada en un navegador lo sigue
     *  leyendo para decidir si pinta la columna. Código nuevo no debería
     *  ramificar por él. */
    vista_nc: boolean;
    /** Si este usuario las ESCRIBE: los tres de Facturación y MASTER. Todos los
     *  demás VEN el número y DESCARGAN el soporte, pero no lo escriben. Lo
     *  decide el servidor, que es quien tiene los roles. */
    puede_cargar_nc: boolean;
    /** Qué decirle a este usuario sobre la NC: cómo anotarla si la escribe, y
     *  quién la carga si no. Nunca es NULL desde el 2026-09-03. */
    aviso_nc: string | null;
    aviso_alcance: string; aviso_aprobacion: string;
    aviso_presupuesto: string; aviso_anuladas: string;
    aviso_decision: string;
    vacio: string; orden_defecto: string; columnas: string[];
    /** Hasta qué día llega la venta con la que se calculó el retorno (el corte
     *  del ETL, no hoy). NULL cuando la consulta no devolvió ni una fila. */
    roi_corte: string | null;
    /** Qué es exactamente la columna «Retorno», para el pie de la tabla. */
    roi_nota: string;
  };
  kpis: {
    negociaciones: number; comprometido: number;
    en_alerta: number; anuladas: number;
    /** El circuito (db/053). `pendientes` NO respeta el filtro de mes ni el de
     *  búsqueda a propósito: es el número de la pestaña. */
    pendientes: number; aprobadas: number; rechazadas: number;
    /** Cuántas de las visibles ya tienen NC anotada (db/072). Se cuenta en las
     *  dos vistas desde el 2026-09-03, igual que se sirve la columna. */
    con_nc: number;
  };
  meses: { ym: number; etiqueta: string }[];
  filas: FilaNego[];
}

export interface NegoAlta {
  alcance: AmbitoNego; clave: string; tipo: string; vendedor_cod: string;
  fecha_inicio: string; fecha_fin: string; estrategia: string; marca_txt: string;
  aplicacion_desc: string; nro_factura?: string;
  /** Informativos, y pueden ser 0: lo que consume presupuesto es el otorgado. */
  cantidad?: string | number; monto_unitario_usd?: string | number;
  monto_otorgado_usd: string | number;
  /** Solo con «Multimarca», que no dice a qué bolsa pertenece. */
  bolsa?: 'COSMETICOS' | 'GAMA';
  /** Solo con la estrategia «Promo B2B»: la promo vigente elegida. Obligatoria
   *  únicamente si hay promos vigentes para el período. */
  promo_b2b_id?: number;
  /** Solo cuando un coordinador reasigna a otro asesor de su zona. */
  presupuesto_vendedor_cod?: string;
  justificacion: string;
  /** Solo en TRADE, y ahí obligatorio (db/255, `src/lib/nego-smart.ts`). */
  objetivo_smart?: string;
  /** Solo en Negociaciones BBK (db/190): los códigos de SKU y la especificación. */
  skus?: string[];
  estrategia_detalle?: string;
  /** Y desde db/196 las UNIDADES de cada SKU, por código. El precio, el costo y
   *  el monto otorgado los calcula el servidor. */
  cantidades?: Record<string, number>;
}

export interface NegoAltaRespuesta {
  ok: true;
  negociacion: {
    id: number;
    /** El N° de control que acaba de emitir el servidor (D40). */
    codigo: string | null;
    fecha_solicitud: string; estado: string; total: number;
    monto_otorgado_usd: number; ventas_3m_usd: number;
    ult_fact_ym: number; ult_fact_usd: number;
    pct_sobre_ult_fact: number | null; pct_consumo_tras: number | null;
    supera_umbral: boolean;
    /** Solo en Negociaciones BBK (db/196): lo que se guardó, para abrir el
     *  reporte de esta negociación sin volver a pedirla. */
    bbk?: {
      lineas: LineaBbk[]; total_usd: number; pct: number | null;
      cliente: string; cod_cli: string; estrategia_detalle: string;
    };
  };
  /**
   * NULL cuando la negociación no salió de ninguna bolsa —hoy, solo una de
   * Trade cuya zona no tiene presupuesto de Trade cargado para el trimestre—,
   * así que no hay saldo que enseñar. No es «no se pudo medir» — un 0 aquí
   * diría que se quedó sin presupuesto. En Trade `vendedor_cod` es NULL (el
   * reparto es por zona) y lo que identifica la bolsa es `destino` + `zona`.
   */
  presupuesto: {
    bolsa: string; cubeta: string; vendedor_cod: string | null;
    destino: 'VENTAS' | 'TRADE'; zona: string;
    monto: number; consumido: number; disponible: number; umbral_pct: number;
  } | null;
  /**
   * Qué pasó con el aviso por correo (db/029, `_lib/correo.ts`). Viene SIEMPRE,
   * y **se pinta**: el alta se registra aunque el correo falle —la negociación
   * ya está escrita y el presupuesto descontado cuando el envío corre—, así que
   * si esto no se enseñara, un buzón caído dejaría a todo el mundo creyendo que
   * el aviso salió. `SIN_TRANSPORTE` = todavía no hay proveedor configurado en
   * el sitio; el envío queda registrado en `core.correo_envio` para reenviar.
   * `avisos` trae los destinatarios que no se pudieron resolver (hoy: las zonas
   * sin coordinador cargado, que son Key Accounts y Nacional/Oficina).
   */
  correo: {
    estado: 'ENVIADO' | 'ERROR' | 'SIN_TRANSPORTE';
    destinatarios: string[];
    avisos: string[];
  };
  aviso: string | null;
}

/* ── El circuito de aprobación (db/053) ───────────────────────────────────── */

/** Lo que se guarda en `estado`. Los BOTONES dicen «Aprobar» y «Rechazar». */
export type DecisionNego = 'Aprobada' | 'No Aprobada';

/** La pestaña del registro. '' = todas, que es lo que pide la app móvil. */
export type NegoFiltroEstado = '' | 'pendientes' | 'decididos';

export interface NegoDecisionRespuesta {
  ok: true;
  decidida: {
    id: number; codigo: string | null;
    /** `null` cuando el coordinador APRUEBA una de Trade (db/256): no es una
     *  decisión final —pasa a Trade Marketing— y no lleva dictamen. */
    estado: string; dictamen: string | null;
    decidido_por: string; decidido_en: string;
    cliente: string; vendedor_cod: string | null;
    monto: number;
    /** Lo que volvió al presupuesto. Siempre 0 al aprobar: aprobar no mueve el
     *  saldo, porque el descuento se hizo al registrar. */
    devuelto: number;
  };
  /** Mismo contrato que el de la anulación, y se PINTA por lo mismo: cuando el
   *  envío corre la decisión ya está escrita, así que callar un fallo dejaría a
   *  todos creyendo que al asesor le avisaron. */
  correo: {
    estado: 'ENVIADO' | 'ERROR' | 'SIN_TRANSPORTE';
    destinatarios: string[];
    avisos: string[];
  };
  aviso: string;
}

export interface NegoAnulacion {
  ok: true;
  anulada: {
    id: number; cliente: string; vendedor_cod: string | null;
    monto: number; devuelto: number;
  };
  /**
   * El aviso de la anulación (db/029, el mismo circuito del alta y con SUS
   * mismos destinatarios). Viene SIEMPRE y **se pinta**: la anulación ya está
   * hecha y el dinero ya volvió cuando el envío corre, así que si esto se
   * callara, un buzón caído dejaría a todo el mundo creyendo que al asesor le
   * avisaron — y con la fila fuera del registro, él no tiene otra forma de
   * enterarse. `SIN_TRANSPORTE` = todavía no hay proveedor en el sitio; el
   * intento queda en `core.correo_envio` para reenviar.
   */
  correo: {
    /**
     * `OMITIDO` = MASTER quitó la casilla del aviso al anular, así que no se
     * intentó enviar nada. NO es un fallo y no se pinta como tal; los otros
     * tres estados sí describen un envío que se intentó.
     */
    estado: 'ENVIADO' | 'ERROR' | 'SIN_TRANSPORTE' | 'OMITIDO';
    destinatarios: string[];
    avisos: string[];
  };
  aviso: string;
}

/**
 * Las tres consultas van por `?seccion=` y NO por sub-rutas, y es a propósito
 * (gotcha 66): una Netlify Function v2 que declara VARIAS rutas en su
 * `config.path` **no entrega los 403 ni los 404 de sus sub-rutas** — la
 * plataforma los toma por «esta ruta no la atiendo» y la petición cae en la ruta
 * base de la propia función. Medido: `/negociaciones/cliente` con un cliente
 * fuera de alcance devolvía 200 con la respuesta del REGISTRO en vez del 403.
 * Los 400 y los 200 sí llegaban, que es lo que lo hacía difícil de ver.
 * `lanzamientos-detalle.mts` ya usaba una sola ruta con parámetros y su 404
 * llega bien; este módulo se pasa a ese patrón.
 */
/** Una evidencia cargada en una negociación (db/056). */
export interface NegoEvidencia {
  id: number;
  nombre: string;
  tipo_mime: string;
  es_imagen: boolean;
  /** El pie de foto. '' cuando no se escribió: el servidor guarda NULL. */
  pie: string;
  tam_bytes: number;
  /** En cuántas piezas está guardado (db/088). 1 = una sola; decide si se abre
   *  con un enlace o hay que bajarlo por partes. */
  partes: number;
  subido_por: string;
  /** 'AAAA-MM-DD HH:MM', ya formateado por el servidor. */
  subido_en: string;
}

export interface NegoEvidencias {
  negociacion_id: number;
  evidencias: NegoEvidencia[];
}

export interface NegoEvidenciaSubida { ok: true; evidencia: NegoEvidencia }

/**
 * Lo que devuelve un borrado de adjuntos, el de las evidencias y el del soporte
 * de la NC (2026-09-17). `no_estaban` son los ids que se pidieron y no se
 * borraron —porque otra pestaña ya los quitó, o porque no eran de esa
 * negociación—: la pantalla lo DICE en vez de callarlo, que un «se eliminaron 2
 * de 3» sin explicar el tercero es peor que un error.
 */
export interface NegoBorradoAdjuntos {
  ok: true;
  borradas: { id: number; nombre: string }[];
  no_estaban: number[];
}

/** Lo que admite el diálogo de archivos al cargar una evidencia. Word y Excel
 *  entraron el 2026-09-17 («debe permitir cargar archivos de excel y de word»);
 *  el PowerPoint NO, que ella no lo pidió y aquí no ha hecho falta nunca —el que
 *  sí lo admite es Planificación Coordinadores (`ADJUNTO_ACEPTA`).
 *
 *  Esto solo filtra el diálogo del navegador: quien arrastre otra cosa la para
 *  el servidor, que mira el CONTENIDO y no el nombre. Y el `.doc`/`.xls` viejo
 *  cabe porque en esta casa todavía circulan libros guardados así. */
export const EVIDENCIA_ACEPTA = 'image/*,.pdf,.docx,.doc,.xlsx,.xls';

/* ── La nota de crédito (db/072; las dos vistas desde el 2026-09-03) ───────
 * Todo lo de aquí abajo lo atiende el endpoint para quien tenga CUALQUIERA de
 * las dos páginas del registro, `nego` o `nego-nc`. Nació exclusivo de la
 * réplica —módulos Facturación y Finanzas— y la usuaria pidió que la NC se vea
 * y se descargue desde el registro de cualquier módulo. Lo que sigue siendo de
 * unos pocos es ESCRIBIR: `guardarNc` y `subirNcArchivo` responden 403 a quien
 * no tenga rol de Facturación (o MASTER). */

/** Un soporte de la NC. Sin `pie`, a diferencia de las evidencias: una nota de
 *  crédito escaneada no necesita que le expliquen qué es. */
export interface NegoNcArchivo {
  id: number;
  nombre: string;
  tipo_mime: string;
  es_imagen: boolean;
  tam_bytes: number;
  /** En cuántas piezas está guardado (db/088). 1 = una sola; decide si se abre
   *  con un enlace o hay que bajarlo por partes. */
  partes: number;
  subido_por: string;
  /** 'AAAA-MM-DD HH:MM', ya formateado por el servidor. */
  subido_en: string;
}

export interface NegoNcArchivos {
  negociacion_id: number;
  archivos: NegoNcArchivo[];
}

export interface NegoNcArchivoSubido {
  ok: true;
  archivo: NegoNcArchivo;
  /** A quién se avisó de que el documento ya está cargado (db/129). El aviso
   *  sale al ADJUNTAR —no al anotar el número—, así que esto viaja aquí y no en
   *  `guardarNc`. La pantalla lo pinta: quien carga tiene que ver a quién le
   *  llegó y, sobre todo, enterarse si NO salió. */
  correo?: { estado: string; destinatarios: string[]; avisos: string[] };
}

export interface NegoNcGuardada {
  ok: true;
  negociacion: {
    id: number;
    /** NULL cuando se acaba de BORRAR el número. */
    nro_nc: string | null;
    nc_tipo: TipoDocNego | null;
    nc_cargado_por: string | null;
    nc_cargado_en: string | null;
    /** El monto de la NC recién guardado, y las dos cifras que explica: el
     *  estimado que sustituyó y el monto con el que queda la negociación
     *  (db/170). Al quitar la nota los tres vuelven a su sitio. */
    nc_monto: number | null;
    monto_estimado: number | null;
    monto_otorgado: number | null;
  };
  /**
   * LA BOLSA QUEDÓ EN NEGATIVO. `null` mientras cabe, o cuando la negociación
   * no consume presupuesto. Campo PROPIO y no cosido al `aviso` a propósito: la
   * usuaria eligió «dejar pasar y avisar», y un sobregiro escondido dentro de
   * una frase verde de «guardado» es exactamente lo que nadie lee.
   */
  sobregiro: string | null;
  aviso: string;
}

/**
 * EL CLIENTE DE NEGOCIACIONES, ATADO A UNA VISTA (db/079, 2026-08-26).
 *
 * Hasta hoy había un solo `negoApi` y todas sus llamadas exigían la página
 * `nego`; solo el registro sabía pedir `?vista=nc`. Eso funcionaba porque TODO
 * usuario con `nego-nc` tenía también `nego` —lo garantizaba db/072 y lo
 * comprobaba su control 5.11—. **Ese invariante se rompió a propósito**: la
 * usuaria pidió el 2026-08-26 que los tres usuarios de Facturación y
 * GERENCIAFINANZAS dejaran de ver Negociaciones Comerciales en TODOS sus
 * módulos y solo les quedara la réplica. Con `nego` fuera de su token, la
 * réplica se les habría roto en la primera llamada —«No se pudo cargar el
 * módulo», porque `catalogos` pide `nego`— y el fallo no habría estado en la
 * columna nueva sino en la pestaña de entrada.
 *
 * Así que la vista deja de ser un parámetro de UNA llamada y pasa a ser la
 * propiedad del CLIENTE: `negoApi` habla como siempre y `negoApiNc` añade
 * `vista=nc` a todas sus peticiones, o sea que cada una se valida contra
 * `nego-nc`. La página elige el cliente por su `variante` UNA vez y ya no hay
 * que acordarse en cada sitio — que es como se olvida una.
 *
 * Las cuatro llamadas de la NC llevaron `vista=nc` fijo mientras la NC fue
 * exclusiva de la réplica. Desde el 2026-09-03 van con la vista del cliente,
 * como el resto: la página `nego` también pide el soporte, y con el parámetro
 * clavado se lo habría pedido al servidor con una página que sus usuarios no
 * tienen.
 */
function clienteNego(vista?: 'nc' | 'bbk') {
  /* Para `api.get`, que arma la cadena de consulta. `api.get` descarta lo que
   * valga `undefined`, así que sin vista el objeto no aporta nada. */
  const V = vista ? { vista } : {};
  /* Para los POST y los enlaces, que llevan la consulta escrita en la ruta.
   * `Q` va detrás de un `?seccion=…` y `Q0` es el que abre la cadena. */
  const Q = vista ? `&vista=${vista}` : '';
  const Q0 = vista ? `?vista=${vista}` : '';
  /* El POST que se le presta a `subirPorPartes` (ver la cabecera del archivo).
   * Va envuelto en una flecha y no como `api.post` a secas para no perder su
   * `this`. */
  const POST: Poster = (ruta, cuerpo) => api.post(ruta, cuerpo);

  return {
  catalogos: () => api.get<NegoCatalogos>('/negociaciones', { seccion: 'catalogos', ...V }),
  buscar: (alcance: AmbitoNego, q: string) =>
    api.get<NegoSugerencias>('/negociaciones', { seccion: 'buscar', alcance, q, ...V }),
  cliente: (alcance: AmbitoNego, clave: string) =>
    api.get<NegoTarjeta>('/negociaciones', { seccion: 'cliente', alcance, clave, ...V }),
  /** `anuladas: '1'` devuelve también las anuladas, y solo funciona para un
   *  MASTER: para cualquier otro rol el servidor lo ignora. `estado` es la
   *  pestaña del circuito; sin él vienen todas, que es lo que espera la app
   *  móvil. */
  /** `vista: 'nc'` es la RÉPLICA (db/072): el mismo registro exigiendo la
   *  página `nego-nc` en vez de `nego`. Desde el 2026-09-03 las dos respuestas
   *  traen las mismas columnas —la NC incluida—: lo único que las separa es
   *  contra qué página se valida la sesión. */
  registro: (p: {
    tipo?: string; mes?: string; q?: string; anuladas?: '1';
    estado?: NegoFiltroEstado;
  } = {}) =>
    api.get<NegoRegistro>('/negociaciones',
      { ...p, ...V } as Record<string, string | undefined>),
  /** Sin argumento, el trimestre EN CURSO —el del formulario y el de siempre—;
   *  con uno, ese trimestre anterior, para ver cómo quedó (2026-10-01). */
  presupuesto: (t?: Trimestre) => api.get<NegoPresupuesto>('/negociaciones',
    { seccion: 'presupuesto', ...(t ? { anio: t.anio, trimestre: t.trimestre } : {}), ...V }),
  /** Solo con `vista: 'bbk'` (db/190): el acumulado de la sección, que no tiene
   *  presupuesto. Con cualquier otra vista el servidor responde 400. */
  acumulado: () => api.get<NegoAcumuladoBbk>('/negociaciones', { seccion: 'acumulado', ...V }),
  crear: (v: NegoAlta) => api.post<NegoAltaRespuesta>('/negociaciones' + Q0, v),
  /** Solo con `vista: 'bbk'` (db/196): lo que el panel del reporte final
   *  necesita —líneas, reporte si ya se confirmó, SKU y precios del cliente—. */
  reporteBbk: (id: number) =>
    api.get<NegoReporteBbk>('/negociaciones', { seccion: 'reporte-bbk', nego: String(id), ...V }),
  /** «Confirmar reporte»: guarda el reporte final y avisa por correo. Una vez. */
  confirmarReporteBbk: (id: number, lineas: { codigo: string; cantidad: number; costo_usd: number }[]) =>
    api.post<NegoReporteBbkRespuesta>('/negociaciones?seccion=reporte-bbk' + Q, { id, lineas }),
  /** Solo MASTER. Devuelve el monto al presupuesto del trimestre y saca la
   *  negociación del registro (sin borrarla: la traza queda). */
  /**
   * La anulación de MASTER. `avisar` es la casilla del panel: con ella
   * quitada la anulación se hace igual —y devuelve el monto igual— pero no
   * sale ningún correo. Por omisión SÍ avisa: no avisar hay que pedirlo.
   */
  anular: (id: number, motivo: string, avisar = true) =>
    api.post<NegoAnulacion>('/negociaciones?seccion=anular' + Q, { id, motivo, avisar }),
  /**
   * La decisión del coordinador — los botones de «Pendientes» (db/053).
   *
   * `motivo` es OBLIGATORIO al rechazar (mín. 10 caracteres, lo comprueba el
   * servidor) y opcional al aprobar. Lo usan las DOS pantallas, la de
   * negociaciones y la de Farmatodo: las dos escriben en la misma tabla y ya
   * comparten la anulación por este mismo camino.
   */
  decidir: (id: number, decision: DecisionNego, motivo = '') =>
    api.post<NegoDecisionRespuesta>('/negociaciones?seccion=decision' + Q,
      { id, decision, motivo }),

  /* ── Evidencias (db/056) ────────────────────────────────────────────────
   * Varios archivos por negociación, cargados por el asesor o el coordinador
   * DESPUÉS del alta. El servidor comprueba el alcance en las tres. */

  /** Las evidencias de UNA negociación. Se pide al desplegar la fila. */
  evidencias: (negociacionId: number) =>
    api.get<NegoEvidencias>('/negociaciones',
      { seccion: 'evidencia', nego: String(negociacionId), ...V }),

  /**
   * Sube UN archivo, SIN TOPE DE PESO (usuaria, 2026-08-26). Sigue yendo de uno
   * en uno —varios en un cuerpo chocarían con el corte de la plataforma— pero
   * ahora, si no cabe en una petición, viaja en piezas de 3 MiB y el servidor lo
   * cose (db/088). La FILA se escribe solo al cerrar: una evidencia a medias no
   * aparece en la lista de nadie.
   */
  subirEvidencia: (negociacionId: number, file: File, pie = '',
    onProgreso?: (hechas: number, total: number) => void) =>
    subirPorPartes<NegoEvidencia>({
      post: POST,
      ruta: '/negociaciones?seccion=evidencia' + Q,
      file,
      clave: 'evidencia',
      extra: { negociacion_id: negociacionId, pie },
      ...(onProgreso ? { onProgreso } : {}),
    }),

  /** El enlace del botón «Ver»: lo abre otra pestaña con la cookie de sesión. */
  evidenciaUrl: (id: number, parte?: number) =>
    `/api/negociaciones?seccion=evidencia&id=${id}${Q}${parte === undefined ? '' : `&parte=${parte}`}`,

  /** Baja una evidencia cosiendo sus piezas: el camino de las que no caben en
   *  UNA respuesta de la plataforma (20 MB). */
  bajarEvidencia: (a: { id: number; nombre: string; partes: number },
    onProgreso?: (hechas: number, total: number) => void) =>
    descargarPorPartes({
      url: (parte) => `/api/negociaciones?seccion=evidencia&id=${a.id}${Q}${parte === undefined ? '' : `&parte=${parte}`}`,
      nombre: a.nombre,
      partes: a.partes,
      ...(onProgreso ? { onProgreso } : {}),
    }),

  /**
   * Quita UNA o VARIAS evidencias de golpe (2026-09-17): «si hay varios
   * archivos cargados y le da a la opción de eliminar evidencia que pueda
   * seleccionar uno o varios archivos a eliminar».
   *
   * Una sola petición y no una por casilla marcada: tres llamadas son tres
   * resultados posibles que la pantalla tendría que juntar, y dejan la lista a
   * medio borrar si la segunda falla. El servidor comprueba el alcance dentro
   * del propio DELETE y borra también las piezas del almacén.
   *
   * `DELETE` con los ids en la CONSULTA y no en el cuerpo: un DELETE con cuerpo
   * es legal pero lo pierden algunos intermediarios, y `api` solo tiene `get` y
   * `post` — como en `borrarEvaluacionGtm`, que es el otro borrado del portal
   * que sale de este archivo.
   */
  borrarEvidencias: (ids: number[]) =>
    pedir<NegoBorradoAdjuntos>(
      `/negociaciones?seccion=evidencia&ids=${ids.join(',')}${Q}`, { method: 'DELETE' }),

  /* ── La nota de crédito (db/072; abierta a las dos vistas el 2026-09-03) ─
   * Las cuatro van con la vista del CLIENTE, como todas las demás: desde que
   * la usuaria pidió que la NC se vea en cualquier módulo, el endpoint las
   * atiende para `nego` y para `nego-nc`, y cada cliente se valida contra su
   * propia página. Llevaban `vista=nc` fijo, y con eso puesto la página `nego`
   * habría pedido el soporte con la página que sus 31 usuarios NO tienen: un
   * 403 en el sitio donde ahora hay que enseñar el archivo. El endpoint
   * comprueba la página por su cuenta dentro de la sección: el candado no puede
   * depender de que el navegador mande bien el parámetro. */

  /**
   * Anota, corrige o QUITA el N° de la nota de crédito. Mandar `''` lo borra
   * —Facturación teclea mal y tiene que poder deshacerlo— y con él se va la
   * traza; quién lo quitó queda en la bitácora. Solo lo atiende para los roles
   * de Facturación y MASTER; para el resto responde 403 con su motivo.
   */
  /** `tipo` es obligatorio al anotar y se ignora al borrar (mandar `''` sigue
   *  quitando el número, y con él se va el tipo). */
  /**
   * `monto` es EL MONTO DE LA NOTA DE CRÉDITO (db/170) y **sustituye** al
   * estimado de la negociación, moviendo la diferencia en el presupuesto del
   * asesor. Es obligatorio cuando `tipo === 'NC'` —el servidor responde 400 sin
   * él— y se ignora en los otros dos casos: al borrar el número y con una
   * FACTURA, que no lleva barra de monto. En esos dos la negociación RECUPERA su
   * estimado, así que pasar `undefined` no es «déjalo como está»: es «vuelve al
   * monto del asesor».
   */
  guardarNc: (negociacionId: number, nroNc: string, tipo: TipoDocNego, monto?: number) =>
    api.post<NegoNcGuardada>('/negociaciones?seccion=nc' + Q,
      { negociacion_id: negociacionId, nro_nc: nroNc, tipo, monto }),

  /** Los soportes de UNA negociación. Se pide al desplegar la fila. */
  ncArchivos: (negociacionId: number) =>
    api.get<NegoNcArchivos>('/negociaciones',
      { seccion: 'nc-archivo', nego: String(negociacionId), ...V }),

  /** Sube UN soporte, SIN TOPE DE PESO y por partes si hace falta — mismo
   *  protocolo que las evidencias (db/088). */
  /** `onCorreo` recibe el aviso que el servidor mandó al asesor y a su
   *  coordinador al quedar cargado el documento (db/129). Es opcional: quien no
   *  lo pase sube igual. */
  subirNcArchivo: (negociacionId: number, file: File,
    onProgreso?: (hechas: number, total: number) => void,
    onCorreo?: (c: NonNullable<NegoNcArchivoSubido['correo']>) => void) =>
    subirPorPartes<NegoNcArchivo>({
      post: POST,
      ruta: '/negociaciones?seccion=nc-archivo' + Q,
      ...(onCorreo
        ? {
          onFinal: (r: Record<string, unknown>) => {
            const c = r.correo as NegoNcArchivoSubido['correo'];
            if (c) onCorreo(c);
          },
        }
        : {}),
      file,
      clave: 'archivo',
      extra: { negociacion_id: negociacionId },
      ...(onProgreso ? { onProgreso } : {}),
    }),

  /** El enlace del botón «Ver» del soporte de la NC: lo abre en otra pestaña. */
  ncArchivoUrl: (id: number, parte?: number) =>
    `/api/negociaciones?seccion=nc-archivo&id=${id}${Q}${parte === undefined ? '' : `&parte=${parte}`}`,

  /** El del botón «Descargar» (2026-09-03). Es la MISMA ruta con `descargar=1`,
   *  que hace que el servidor mande `content-disposition: attachment` en vez de
   *  `inline`: el navegador lo guarda en vez de abrirlo. Son dos enlaces y no
   *  uno porque son dos actos distintos —mirar la nota y guardarla—, y porque
   *  ver un PDF empotrado no deja copia en el equipo de quien lo mira. Solo
   *  vale para lo que cabe en UNA respuesta; lo que no, sigue yendo por
   *  `bajarNcArchivo`, que cose las piezas en el navegador. */
  ncArchivoDescargaUrl: (id: number) =>
    `/api/negociaciones?seccion=nc-archivo&id=${id}&descargar=1${Q}`,

  /** Baja un soporte de NC cosiendo sus piezas. */
  bajarNcArchivo: (a: { id: number; nombre: string; partes: number },
    onProgreso?: (hechas: number, total: number) => void) =>
    descargarPorPartes({
      url: (parte) => `/api/negociaciones?seccion=nc-archivo&id=${a.id}${Q}${parte === undefined ? '' : `&parte=${parte}`}`,
      nombre: a.nombre,
      partes: a.partes,
      ...(onProgreso ? { onProgreso } : {}),
    }),

  /** Quita uno o varios soportes de la NC. El gemelo de `borrarEvidencias`, con
   *  el candado estrecho del módulo: solo Facturación y MASTER; a los demás el
   *  servidor les responde 403 con su motivo. */
  borrarNcArchivos: (ids: number[]) =>
    pedir<NegoBorradoAdjuntos>(
      `/negociaciones?seccion=nc-archivo&ids=${ids.join(',')}${Q}`, { method: 'DELETE' }),
  };
}

/** El cliente de siempre: exige la página `nego`. Lo usan la sección comercial,
 *  el Simulador y `NegoFarmatodo` (que comparte la anulación y la decisión). */
export const negoApi = clienteNego();

/** El de la réplica de Notas de Crédito: TODA su llamada exige `nego-nc`. Es lo
 *  que hace que los 4 usuarios sin `nego` puedan usar la página entera. */
export const negoApiNc = clienteNego('nc');

/** El de NEGOCIACIONES BBK (db/190): TODA su llamada lleva `vista=bbk`, que exige
 *  la página `nego-bbk` y —esto es lo nuevo— recorta al REGISTRO BBK. Una
 *  llamada de esa página hecha con `negoApi` a pelo no daría error: pintaría las
 *  negociaciones COMERCIALES, que es peor. Por eso viaja como prop. */
export const negoApiBbk = clienteNego('bbk');

/* ─────────── Simulador de Promociones (`nego-sim`) ───────────
 * Es una página de la MISMA sección que `nego`, y por eso su endpoint sirve solo
 * lo que le es propio —los productos con precio y costo— mientras que el
 * cliente, su facturación mensual y el presupuesto los pide a `negoApi`. Las dos
 * pestañas leen la misma base a propósito: si el simulador trajera sus propias
 * ventas, el mismo cliente mostraría dos cifras distintas según dónde se mire.
 */
export interface PromoProducto {
  id: number; empresa_id: number; empresa: string;
  sku: string; marca: string; linea: string | null; tipo: string | null;
  precio: number; costo: number; margen: number;
  /** TRUE = el costo es un factor plano, no el de finanzas. Se PINTA. */
  costo_estimado: boolean;
}

export interface SimuladorCatalogos {
  meta: {
    modulo: string; pagina: string; titulo: string; subtitulo: string;
    piso: number;
    aviso_cliente: string; aviso_costo: string;
    aviso_simulacion: string; aviso_piso: string;
    mecanicas: {
      id: string; etiqueta: string; param: string; defecto: number; ayuda: string;
    }[];
  };
  empresas: {
    empresa_id: number; codigo: string; nombre: string;
    bolsa: string; piso: number; productos: number;
    costo_estimado: boolean; fecha_corte: string | null; fuente: string | null;
    margen_min: number; margen_max: number;
  }[];
  productos: PromoProducto[];
}

export const simuladorApi = {
  catalogos: () => api.get<SimuladorCatalogos>('/nego-simulador', { seccion: 'catalogos' }),
};

/**
 * El corte de datos de DHALIA. Endpoint propio (`dhalia-corte.mts`) **que no
 * exige ninguna página**, porque la asistente lo pedía por `/resumen/diario` y
 * eso dejaba a `ALMACENYDESPACHO` —el único rol sin la página `resumen`— sin
 * poder responder «¿hasta cuándo hay datos?», que es uno de sus cinco chips de
 * serie (`04_DOCS/ESTADO_DHALIA.md` §6.2 y §9, punto 6).
 *
 * Devuelve **los dos rangos**: el global del cubo (el que enseña el v88) y el
 * del alcance del usuario, intersecado en SQL. La decisión D-2 —cuál de los dos
 * se enseña— sigue siendo de la usuaria; el endpoint no la toma, la habilita.
 */
export interface DhaliaCorte {
  global: { min: string; max: string };
  alcance: { min: string | null; max: string | null };
  acotado: boolean;
  usuario: string;
}

/**
 * El modo de IA en línea de DHALIA — la D-1, respondida el 2026-08-14. El
 * navegador habla SOLO con `/api/dhalia/chat` (mismo origen: el CSP con
 * `connect-src 'self'` no se toca); es la función `dhalia-chat.mts` la que
 * llama a la API de Claude con la clave en variables de entorno de Netlify.
 * `ia()` sustituye al `/health` del v88: dice si el despliegue tiene clave.
 */
export interface DhaliaIaEstado {
  disponible: boolean;
  modelo: string | null;
  /** Solo diagnóstico: si la clave la puso el AI Gateway de Netlify. */
  pasarela?: boolean | null;
}
export interface DhaliaChatRespuesta { texto: string }
export interface DhaliaChatCuerpo {
  pregunta: string;
  hist: { role: 'user' | 'assistant'; content: string }[];
  secciones: string[];
  corte?: string;
  /** Lo que el motor local habría contestado, en texto plano. Viaja como
   * material para que la IA lo diga con sus palabras, no como respuesta. */
  pista?: string;
}

export const dhaliaApi = {
  corte: () => api.get<DhaliaCorte>('/dhalia/corte'),
  ia: () => api.get<DhaliaIaEstado>('/dhalia/chat'),
  chat: (cuerpo: DhaliaChatCuerpo) => api.post<DhaliaChatRespuesta>('/dhalia/chat', cuerpo),
};

/* ─────────── Go-To-Market (`gtm`) ───────────
 * El dataset es un extracto EXTERNO congelado al 2026-07-24 (dos semanas antes
 * del corte del portal): NO se deriva de la base de ventas y no se recalcula
 * (PARIDAD_GTM.md §4, D-1). Tres cosas que hay que respetar al pintar:
 *  · `meta.aviso_corte`, `aviso_proyeccion` y `aviso_alcance` SE PINTAN. Ahí
 *    viajan el corte distinto, el H-1 (la proyección son cajas del período
 *    rotuladas «/mes») y el endurecimiento del alcance (D-5).
 *  · Las formas replican el literal: `sales[cod][marca] = {total, lineas}`,
 *    con un nivel {total, lineas} entre la marca y la sublínea (gotcha 62 del
 *    módulo — un recorrido de un nivel menos da 0 filas sin error).
 *  · `proj` viene del UNIVERSO COMPLETO aunque el usuario esté acotado: la
 *    referencia son «clientes similares», como el PROJ global del v88.
 */

export interface GtmCliente {
  name: string; lbl: string; region: string | null;
  vend: string; coord: string; seg: string; mode: string | null;
  pend: boolean | null; sale: boolean; canal: string | null;
  ciudad: string | null; estado: string | null;
  clasifEco: string | null; clasifCred: string | null;
  limCred: string | null; diasCred: string | null;
  /** db/103. NO viene del extracto: la escribe la aprobacion de una evaluacion. */
  aliado: string | null;
}

export interface GtmLinea { t: number; v: number[] }
export interface GtmVentaMarca { total: number; lineas: Record<string, GtmLinea> }
export type GtmSales = Record<string, Record<string, GtmVentaMarca>>;
export type GtmProj = Record<string, Record<string, Record<string, { avg: number; n: number }>>>;
export interface GtmCat { mode: string | null; canal: string | null; port: Record<string, string[]> }

export interface GtmDetalle {
  meta: {
    period: string; months: string[]; nmonths: number;
    generated: string; source: string;
    segList: string[]; clasifEco: string[]; clasifCred: string[];
    aviso_corte: string; aviso_proyeccion: string; aviso_alcance: string | null;
    alcance_no_aplicable: string[]; regiones_sin_mapa: string[];
  };
  coords: Record<string, { region: string; vends: string[] }>;
  coordByVend: Record<string, string>;
  subz: Record<string, string>;
  mc: Record<string, string>;
  cat: Record<string, GtmCat>;
  clients: Record<string, GtmCliente>;
  sales: GtmSales;
  proj: GtmProj;
  logos: Record<string, string>;
}

export interface GtmAcc {
  reclas?: { to: string };
  aliado?: { to: string };
  eco?: { to: string };
  cred?: { clasif: string; lim: string; dias: string };
}

/**
 * Los cuatro estados del circuito (db/103). No hay 'Pendiente'/'Resuelta': la
 * bandeja dejó de ser una libreta el 2026-08-31.
 *
 *   Aplicada             la reclasificación de tipo, ya escrita en la ficha
 *   Pendiente aprobacion espera al coordinador (sin tilde, como en `negociacion`)
 *   Aprobada             el coordinador aprobó, y aprobar es lo que aplica
 *   No Aprobada          rechazada, con `motivo_decision`
 */
export type GtmEstado = 'Aplicada' | 'Pendiente aprobacion' | 'Aprobada' | 'No Aprobada';

export interface GtmEvaluacion {
  id: number; fecha: string; cod_cli: string; cliente: string;
  seg: string; coord: string; vend: string; region: string;
  acc: GtmAcc; prioridad: 'Media' | 'Alta' | 'Baja';
  evaluador: string; motivo: string; estado: GtmEstado;
  creado_por: string;
  decidido_por: string | null;
  motivo_decision: string | null;
  decidido_el: string | null;
  aplicado_el: string | null;
  /** Lo decide el SERVIDOR, que es quien sabe los roles y el alcance: la página
   *  no vuelve a deducir quién aprueba (si lo hiciera habría dos reglas). */
  puede_decidir: boolean;
}

export interface GtmEvalRespuesta {
  ok: true;
  evaluacion: GtmEvaluacion;
  /** Lo que YA se escribió en la ficha del cliente, redactado por el servidor. */
  aplicados: string[];
  /** Lo que quedó esperando al coordinador. */
  pendientes: string[];
  correo: { estado: string; destinatarios: string[]; avisos: string[] };
}

export interface GtmNota {
  seg: string; marca: string; texto: string; fecha: string;
  actualizado_por: string | null;
}

/* ─────────── Resumen General: las 3 páginas del cubo _RAW ───────────
 * `evolucion`, `forecast` y `descarga` corren sobre `core.mv_cubo_raw`
 * (el cubo mensual de Marcas), NO sobre el diario del Resumen ya portado.
 * Gate: 04_DOCS/PARIDAD_RESUMEN_3.md. Tres cosas que se PINTAN:
 *  · `meta.aviso_*`: la barra de filtros propia (D-E1), las etiquetas canon
 *    (D-E3) y la fila Total del YoY con sus dos importes no comparables.
 *  · Los `avisos` del forecast (estacionalidad de Ago–Dic con una sola
 *    observación; proyección $0 recortada) son AÑADIDOS del porte.
 *  · El export marca la columna «Categoría» como añadida (hallazgo 8).
 */

export interface EvolucionRespuesta {
  meta: {
    titulo: string; subtitulo: string; labels: string[]; mesesN: string[];
    lastM: number; haySel: boolean;
    filtros: { empresa?: string; region?: string; vend?: string; marca?: string };
    aviso_seleccion: string; aviso_etiquetas: string; aviso_total_yoy: string;
  };
  share: { uv: number[]; uc: number[]; fv: number[]; fc: number[] };
  yoy: { a25v: number[]; a26v: number[]; a25c: number[]; a26c: number[]; lastM: number };
  marca: {
    tot: { v: number[]; c: number[] };
    ventas: { name: string; arr: number[] }[];
    cajas: { name: string; arr: number[] }[];
  };
  catalogos: { empresas: string[]; regiones: string[]; vendedores: string[]; marcas: string[] };
}

export interface ForecastOk {
  ok: true; nombre: string; h: number; metodo: string;
  labels: string[]; histN: number;
  hv: number[]; hc: number[]; projV: number[]; projC: number[];
  lo: number[]; hi: number[]; futL: string[]; rmse: number;
  kpis: { proyectadas: number; promedio: number; vsUltimos: number | null; rmse: number };
  coef: {
    ventas: { a: number; b: number; sIdx: Record<number, number>; rmse: number };
    cajas: { a: number; b: number; sIdx: Record<number, number>; rmse: number };
  };
  avisos: string[]; aviso_banda: string;
}
export interface ForecastInsuficiente {
  ok: false; nombre: string; cnt: number; mensaje: string; toast: string;
}
export type ForecastRespuesta = ForecastOk | ForecastInsuficiente;

export interface DescargaPreview {
  meta: {
    titulo: string; subtitulo: string; head: string[];
    /** El primer y el último mes CARGADOS en el cubo. No depende del filtro. */
    rango: string;
    /** La ventana PEDIDA (`desde`/`hasta`), recortada a los meses que existen. */
    periodo?: string;
    /** Topes del selector de período, en el formato del `<input type="month">`. */
    rango_ym?: { min: string; max: string };
    xp_guard: number; aviso_categoria: string; aviso_etiquetas: string;
  };
  registros: number; ventas: number; cajas: number;
  filas: (string | number)[][];
}

/** El período de la Descarga: `null` cuando el cubo está vacío. */
export interface DescargaPeriodos { min: string | null; max: string | null }

export const resumen3Api = {
  evolucion: (p: { empresa?: string; region?: string; vend?: string; marca?: string } = {}) =>
    api.get<EvolucionRespuesta>('/resumen/evolucion', p as Record<string, string | undefined>),
  fcOpciones: (dim: string) =>
    api.get<{ opciones: { label: string; sub?: string }[] }>('/resumen/forecast', { seccion: 'opciones', dim }),
  fcSubs: (val: string) =>
    api.get<{ sucursales: { nombre: string; v: number }[] }>('/resumen/forecast', { seccion: 'subs', val }),
  fcCalcular: (p: { dim: string; val?: string; sub?: string; h: number }) =>
    api.get<ForecastRespuesta>('/resumen/forecast', p as Record<string, string | number | undefined>),
  ddOpciones: (dim: string) =>
    api.get<{ opciones: { label: string; sub?: string; valor?: string }[] }>('/resumen/descarga', { seccion: 'opciones', dim }),
  ddPeriodos: () =>
    api.get<DescargaPeriodos>('/resumen/descarga', { seccion: 'periodos' }),
  ddPreview: (p: { dim?: string; val?: string; desde?: string; hasta?: string } = {}) =>
    api.get<DescargaPreview>('/resumen/descarga', { previsualizar: 1, ...p } as Record<string, string | number | undefined>),
};

export const gtmApi = {
  detalle: () => api.get<GtmDetalle>('/gtm/detalle'),
  evaluaciones: () => api.get<{ evaluaciones: GtmEvaluacion[] }>('/gtm/evaluaciones'),
  evaluar: (v: {
    cod: string; acc: GtmAcc; prioridad: string; evaluador: string; motivo: string;
  }) => api.post<GtmEvalRespuesta>('/gtm/evaluaciones', v),
  /** El coordinador decide (db/103). `motivo` es OBLIGATORIO al rechazar y el
   *  servidor lo exige otra vez: aqui solo se evita el viaje. */
  evalDecidir: (id: number, decision: 'Aprobada' | 'No Aprobada', motivo = '') =>
    pedir<{ ok: true; estado: string; aplicados: string[];
      correo: { estado: string; destinatarios: string[]; avisos: string[] } }>(
      '/gtm/evaluaciones', {
        method: 'PATCH', body: JSON.stringify({ id, decision, motivo }),
      }),
  evalBorrar: (id: number) =>
    pedir<{ ok: true }>(`/gtm/evaluaciones?id=${id}`, { method: 'DELETE' }),
  notas: () => api.get<{ notas: GtmNota[] }>('/gtm/notas'),
  notaGuardar: (v: { seg: string; marca: string; texto: string }) =>
    api.post<{ ok: true; nota: GtmNota }>('/gtm/notas', v),
};

/* ══════════════════ Bitácora de usuarios (`bitacora`) ══════════════════
 * El módulo Contraloría. Lee `auth.v_bitacora` a través de
 * `netlify/functions/bitacora.mts`. Ver 04_DOCS/ESPEC_MODULO_BITACORA.md.
 *
 * Las marcas de tiempo viajan en ISO con zona y se pintan con la del navegador
 * —que es la de Caracas—; los filtros de fecha son días naturales y el servidor
 * los interpreta en esa misma zona. Sin ese cuidado, las entradas de la tarde
 * caerían en el día siguiente (Neon corre en UTC).
 */
export interface FilaBitacora {
  id: number;
  ts: string;
  /** El username, o el TECLEADO cuando el intento fue contra una cuenta que no existe. */
  usuario_txt: string | null;
  nombre: string | null;
  etiqueta: string;
  categoria: 'sesion' | 'escritura' | 'descarga' | 'seguridad' | 'otro';
  /** `prueba` = un arnés o un `netlify dev` contra esta base (db/126). */
  origen: 'web' | 'movil' | 'api' | 'tarea' | 'claude' | 'prueba' | 'sin dato';
  resumen: string | null;
  ip: string | null;
  /** Si trae algo, la acción NO fue del titular: entró con la clave maestra. */
  actor_maestro: string | null;
}

export interface SesionBitacora {
  username: string | null;
  nombre: string | null;
  entro: string;
  /** `prueba` = un arnés o un `netlify dev` contra esta base (db/126). */
  origen: 'web' | 'movil' | 'api' | 'tarea' | 'claude' | 'prueba' | 'sin dato';
  /** `null` = no pulsó Salir. No es un fallo del registro: ver la espec §6. */
  salio: string | null;
  /** Cuándo se le venció la cookie, calculado (entrada + 8 h). */
  vence: string;
  acciones: number;
  /** `true` = hubo `logout`, así que el final está MEDIDO y no calculado. */
  cerrada: boolean;
  /**
   * Minutos CONECTADO SIN REGISTRAR NADA: la suma de los huecos que pasan del
   * umbral entre dos actos consecutivos. En una sesión sin salida (`cerrada`
   * falso) es un MÍNIMO — el rabo hasta el vencimiento calculado no se cuenta.
   *
   * ⚠️ Mide «sin escribir ni descargar», NO «sin trabajar»: consultar el portal
   * no deja rastro en la bitácora. La pantalla y el Excel lo dicen así.
   */
  inactivo_min: number;
  /** El hueco más largo de la sesión, en minutos, pase o no del umbral. */
  pausa_max_min: number;
  /** Cuándo empezó ese hueco (ISO), o `null` si no hubo ninguno. */
  pausa_max_desde: string | null;
  /** El umbral con el que se calculó, para que la fila diga con qué regla. */
  umbral_min: number;
}

export interface MetaBitacora {
  usuarios: { username: string; nombre: string; activo: boolean }[];
  categorias: { categoria: string; n: number }[];
  origenes: { origen: string; n: number }[];
  rango: { desde: string | null; hasta: string | null; total: number };
  horas_sesion: number;
}

export interface ParamsBitacora {
  d0?: string; d1?: string;
  usuario?: string; categoria?: string; origen?: string; q?: string;
  pag?: number;
  /** Solo Sesiones: minutos sin actos a partir de los cuales cuenta la pausa. */
  umbral?: number;
  /**
   * `'1'` = enseña también las filas de PRUEBA (db/126): lo que hizo un arnés o
   * un `netlify dev` contra esta base, que la bitácora guardaba con el nombre
   * de un usuario real. Por defecto se esconden y el servidor dice cuántas en
   * `ocultas` — esconder sin decirlo sería peor que el ruido.
   */
  pruebas?: '1';
}

/**
 * El exporte a Excel: los MISMOS filtros, sin paginar. `total` es lo que trae
 * el archivo y `truncado` avisa de que el tope del servidor recortó — la hoja
 * lo escribe en su cabecera (`src/lib/bitacora-xls.ts`).
 */
export interface ExporteBitacora<T> {
  ventana: { desde: string; hasta: string };
  filas: T[];
  total: number;
  tope: number;
  truncado: boolean;
  horas_sesion?: number;
  /** Solo en el exporte de Sesiones: el umbral de pausa con el que se calculó. */
  umbral_min?: number;
}

export const bitacoraApi = {
  meta: () => api.get<MetaBitacora>('/bitacora', { meta: 1 }),
  movimientos: (p: ParamsBitacora = {}) =>
    api.get<{
      vista: 'movimientos'; ventana: { desde: string; hasta: string };
      pagina: number; total: number; por_pagina: number; filas: FilaBitacora[];
      /** Cuántas filas de prueba se dejaron fuera con estos filtros (db/126). */
      ocultas: number;
    }>('/bitacora', { vista: 'movimientos', ...p } as Record<string, string | number | undefined>),
  sesiones: (p: ParamsBitacora = {}) =>
    api.get<{
      vista: 'sesiones'; ventana: { desde: string; hasta: string };
      pagina: number; hay_mas: boolean; filas: SesionBitacora[];
      horas_sesion: number; umbral_min: number;
      /** Cuántas JORNADAS de prueba se dejaron fuera (db/126). */
      ocultas: number;
    }>('/bitacora', { vista: 'sesiones', ...p } as Record<string, string | number | undefined>),

  /* `pag` se descarta a propósito: el exporte no es la página que se está
   * viendo, es todo lo que casa con los filtros. */
  exportarMovimientos: ({ pag: _pag, ...p }: ParamsBitacora = {}) =>
    api.get<ExporteBitacora<FilaBitacora>>('/bitacora',
      { vista: 'movimientos', todo: 1, ...p } as Record<string, string | number | undefined>),
  exportarSesiones: ({ pag: _pag, ...p }: ParamsBitacora = {}) =>
    api.get<ExporteBitacora<SesionBitacora>>('/bitacora',
      { vista: 'sesiones', todo: 1, ...p } as Record<string, string | number | undefined>),
};
